import type { Translation } from "./en";

const es: Translation = {
  nav: {
    loggedOutToast: "Tu sesión se ha cerrado.",
    openMenu: "Abrir menú",
    closeMenu: "Cerrar menú",
    platformSectionLabel: "Plataforma",
    expandSidebar: "Expandir barra lateral",
    collapseSidebar: "Contraer barra lateral",
    collapse: "Contraer",
    resizeSidebar: "Ajustar ancho de la barra lateral",
    dashboard: "Panel",
    customers: "Clientes",
    suppliers: "Proveedores",
    sales: "Ventas",
    expenses: "Gastos",
    catalog: "Catálogo",
    inventory: "Inventario",
    employees: "Empleados",
    businessSettings: "Configuración",
    platformAdmin: "Admin. Plataforma",
    accountSettings: "Mi cuenta",
    logout: "Cerrar sesión",
    calendar: "Calendario",
    goals: "Metas",
    notifications: "Notificaciones",
  },
  auth: {
    brand: "Adam",
    layout: {
      tagline: "Tus ventas, clientes, inventario y metas — un solo lugar, en vivo.",
      backHome: "Volver al inicio",
    },
    password: {
      show: "Mostrar contraseña",
      hide: "Ocultar contraseña",
    },
    login: {
      subtitle: "Inicia sesión en tu cuenta",
      email: "Correo",
      emailPlaceholder: "tu@correo.com",
      password: "Contraseña",
      passwordPlaceholder: "Ingresa tu contraseña",
      submit: "Iniciar sesión",
      submitting: "Ingresando…",
      forgotLink: "¿Olvidaste tu contraseña?",
      signupPrompt: "¿No tienes cuenta?",
      signupLink: "Regístrate",
      welcomeToast: "¡Bienvenido de nuevo!",
      genericError: "No se pudo iniciar sesión. Inténtalo de nuevo.",
      invalidCredentials: "Correo o contraseña incorrectos.",
      notVerified: "Tu cuenta aún no está verificada. Busca el enlace de verificación en tu correo.",
      tooManyAttempts: "Demasiados intentos. Espera un minuto e inténtalo de nuevo.",
      resendVerification: "Reenviar correo de verificación",
      verificationSent: "Correo de verificación enviado. Revisa tu bandeja.",
    },
    register: {
      subtitle: "Crea tu cuenta",
      firstName: "Nombre",
      lastName: "Apellido",
      email: "Correo",
      emailPlaceholder: "tu@correo.com",
      password: "Contraseña",
      passwordPlaceholder: "Mín. 8 caracteres",
      confirmPassword: "Confirma la contraseña",
      confirmPlaceholder: "Repite tu contraseña",
      submit: "Crear cuenta",
      submitting: "Creando cuenta…",
      loginPrompt: "¿Ya tienes una cuenta?",
      loginLink: "Inicia sesión",
      inviteHint: "¿Te invitaron a un equipo? Abre el enlace de tu correo de invitación para unirte.",
      successTitle: "Revisa tu correo",
      successWhy: "Te enviamos un enlace de verificación para mantener tu cuenta segura.",
      successWhen: "Suele llegar en menos de un minuto. Si no lo ves, revisa la carpeta de correo no deseado.",
      successExpiry: "El enlace de verificación expira en 3 días.",
      emailSendFailed:
        "No pudimos enviar el correo en este momento. Usa el botón de reenviar en un momento — tu cuenta sí fue creada.",
      successResend: "Reenviar correo de verificación",
      successResending: "Enviando…",
      successResendSent: "¡Enviado! Revisa tu bandeja otra vez.",
      successBackLink: "Volver al inicio de sesión",
      successToast: "¡Cuenta creada! Revisa tu correo para verificar.",
      genericError: "El registro falló.",
      validation: {
        emailRequired: "El correo es obligatorio.",
        firstNameRequired: "El nombre es obligatorio.",
        lastNameRequired: "El apellido es obligatorio.",
        passwordTooShort: "La contraseña debe tener al menos 8 caracteres.",
        passwordsDoNotMatch: "Las contraseñas no coinciden.",
        emailExists: "Ya existe una cuenta con este correo. Inicia sesión o restablece tu contraseña.",
      },
    },
    forgot: {
      subtitle: "Restablecer tu contraseña",
      email: "Correo",
      emailPlaceholder: "tu@correo.com",
      submit: "Enviar enlace",
      submitting: "Enviando…",
      backLink: "Volver al inicio de sesión",
      successTitle: "Revisa tu correo",
      successBody: "Si existe una cuenta para {{email}}, te enviamos un enlace para restablecer la contraseña.",
      successExpiry: "El enlace de restablecimiento expira en 3 días.",
    },
    reset: {
      subtitle: "Elige una nueva contraseña",
      newPassword: "Nueva contraseña",
      passwordPlaceholder: "Mín. 8 caracteres",
      passwordTooShort: "La contraseña debe tener al menos 8 caracteres.",
      submit: "Restablecer contraseña",
      submitting: "Restableciendo…",
      invalidTitle: "Enlace de restablecimiento inválido",
      invalidBody: "Este enlace para restablecer la contraseña no es válido. Solicita uno nuevo.",
      requestNewLink: "Solicitar nuevo enlace",
      failedExpired:
        "No se pudo restablecer. El enlace pudo haber expirado (dura 3 días) o la contraseña era demasiado débil — solicita un nuevo enlace y prueba una contraseña más fuerte.",
      failedGeneric: "No se pudo restablecer. Inténtalo de nuevo.",
      successTitle: "¡Contraseña restablecida!",
      successBody: "Tu contraseña fue actualizada. Ya puedes iniciar sesión.",
      signinLink: "Iniciar sesión",
    },
    verify: {
      loading: "Verificando tu correo…",
      successTitle: "¡Correo verificado!",
      signinLink: "Iniciar sesión",
      errorTitle: "Verificación fallida",
      errorBodyMissing: "A este enlace le faltan datos. Abre el correo de nuevo y haz clic directamente en el botón de verificación.",
      errorBodyExpired: "Este enlace de verificación no es válido o ha expirado. Usa el botón de abajo o regístrate de nuevo.",
      registerLink: "Volver a registrarte",
      resendButton: "Reenviar correo de verificación",
      resending: "Enviando…",
      resendSent: "¡Enviado! Revisa tu bandeja otra vez.",
      resendCooldown: "Puedes pedir otro en {{seconds}}s",
      resendPrompt: "¿Necesitas un nuevo enlace? Ingresa tu correo:",
    },
    joinBusiness: {
      loadingInvite: "Cargando tu invitación…",
      invalidTitle: "Invitación inválida o expirada",
      invalidBody:
        "Este enlace de invitación ya no es válido. Pide a tu administrador que envíe uno nuevo.",
      loginLink: "Ir al inicio de sesión",
      title: "Únete a {{business}}",
      invitedBy: "{{inviter}} te invitó a unirte como {{role}}.",
      invitedByNoName: "Te han invitado a unirte como {{role}}.",
      emailLocked: "Esta es la dirección a la que se envió tu invitación.",
      submit: "Crear cuenta y unirme",
      submitting: "Uniéndote…",
      existingBody:
        "Ya existe una cuenta para {{email}}. Acepta la invitación para unirte a {{business}}.",
      acceptCta: "Aceptar invitación",
      accepting: "Uniéndote…",
      successTitle: "Revisa tu correo",
      successBody:
        "Enviamos un enlace de verificación a {{email}}. Verifícalo y luego inicia sesión para empezar.",
      acceptedTitle: "¡Estás dentro!",
      gotoLogin: "Iniciar sesión",
      genericError: "Algo salió mal. Inténtalo de nuevo.",
      verifyFirstTitle: "Verifica tu correo primero",
      verifyFirstBody:
        "Ya existe una cuenta para {{email}} pero no está verificada. Verifícala desde el correo que te enviamos y luego inicia sesión — ya quedaste listo para unirte a {{business}}.",
      resendVerification: "Reenviar correo de verificación",
      alreadyMember:
        "Esta cuenta ya pertenece a otro negocio. Cada cuenta solo puede ser miembro de un negocio.",
      expired: "Esta invitación ha expirado. Pide a tu administrador que envíe una nueva.",
    },
  },
  help: {
    title: "Ayuda",
    gettingStarted: "Primeros pasos",
    gettingStartedHint: "Reabre la lista de configuración",
    documentation: "Documentación",
    documentationHint: "Cómo funciona Adam",
    checklistModal: {
      title: "Primeros pasos",
      subtitle: "Completa esto para terminar de configurar tu espacio de trabajo.",
      allDone: "¡Todo listo! Completaste todos los pasos. 🎉",
      close: "Cerrar",
    },
    doc: {
      title: "Documentación",
      subtitle: "Cómo funciona Adam, módulo por módulo.",
      groups: {
        start: "Comienza aquí",
        modules: "Módulos",
      },
      onThisPage: "En esta página",
    },
  },
  common: {
    avatarAlt: "Avatar",
    uploadImage: "Subir imagen",
    dismiss: "Descartar",
    save: "Guardar",
    create: "Crear",
    cancel: "Cancelar",
    close: "Cerrar",
    delete: "Eliminar",
    edit: "Editar",
    search: "Buscar",
    loading: "Cargando",
    actions: "Acciones",
    page: "Página",
    of: "de",
    previous: "Anterior",
    next: "Siguiente",
    noResults: "Sin resultados",
    total: "Total",
    exportCsv: "Exportar CSV",
    addedBy: "Agregado por",
    thisMonth: "Este mes",
    saving: "Guardando...",
    confirm: "Confirmar",
    createdBy: "Creado por",
    updatedBy: "Modificado por",
    deleting: "Eliminando…",
    pageOf: "Página {{page}} de {{total}}",
    view: "Ver",
    none: "Ninguno",
    selectOption: "Seleccionar…",
  },
  payStatusActions: {
    cancel: "Cancelar",
    makePayment: "Hacer pago",
    markAsPaid: "Marcar como pagado",
  },
  status: {
    draft: "Borrador",
    confirmed: "Confirmado",
    cancelled: "Cancelado",
    recorded: "Registrado",
    approved: "Aprobado",
    rejected: "Rechazado",
    active: "Activo",
    inactive: "Inactivo",
    unpaid: "Sin pagar",
    partial: "Parcial",
    paid: "Pagado",
  },
  dashboard: {
    agingDays: "días",
    heatmapDow: ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"],
    incomeUncategorized: "Sin categoría",
    vsLabel: "vs",
    minAbbrev: "mín",
    title: "Panel",
    totalCustomers: "Total de clientes",
    confirmedRevenue: "Ingresos confirmados",
    confirmedSalesCount: "Ventas confirmadas",
    newCustomers: "Clientes nuevos",
    confirmedSales: "Ventas confirmadas",
    confirmedCount: "Confirmadas",
    newThisMonth: "+{{count}} este mes",
    revenueChart: "Ingresos vs. Gastos",
    topCustomers: "Mejores clientes",
    topCustomersSub: "Por ingresos confirmados en el período",
    customer: "Cliente",
    totalSpent: "Total gastado",
    noChartData: "Sin datos para este período.",
    legendRevenue: "Ingresos",
    legendExpenses: "Gastos",
    last7days: "7d",
    last30days: "30d",
    last90days: "90d",
    last365days: "1yr",
    customRange: "Personalizado",
    today: "Hoy",
    yesterday: "Ayer",
    last7daysLong: "Últimos 7 días",
    last30daysLong: "Últimos 30 días",
    last90daysLong: "Últimos 90 días",
    lastYear: "Último año",
    fullHistory: "Historial completo",
    thisMonth: "Este mes",
    lastMonth: "Mes pasado",
    dateRangeApply: "Aplicar",
    dateRangeClear: "Limpiar",
    dateRange: "Rango de fechas",
    dateRangeStart: "Inicio",
    dateRangeEnd: "Fin",
    cumWithin: "En el rango",
    cumAllTime: "Histórico",
    cumLegend: "Rango",
    viewModeLegend: "Vista",
    granularityLegend: "Granularidad",
    kpisTitle: "Resumen",
    trendsTitle: "Tendencias",
    newInPeriod: "+{{count}} en el período",
    expensesApproved: "Gastos aprobados",
    expensesPending: "Gastos pendientes",
    expensesApprovedSub: "Aprobados en 30 días",
    expensesPendingSub: "En espera de aprobación",
    catalogActive: "Artículos activos",
    catalogValue: "Valor del catálogo",
    catalogActiveSub: "Productos y servicios activos",
    catalogValueSub: "Suma de precios activos",
    employeesActive: "Equipo activo",
    employeesActiveSub: "Activos en tu equipo",
    analyticsTitle: "Análisis",
    forecastTitle: "Pronósticos",
    cumulativeChart: "Ingresos y gastos acumulados",
    unifiedTrendChart: "Ingresos y gastos",
    viewModePeriod: "Por período",
    viewModeCumulative: "Acumulado",
    salesByCategoryChart: "Ventas por categoría",
    expenseBreakdownChart: "Desglose de gastos",
    customerGrowthChart: "Crecimiento de clientes",
    avgSaleValueChart: "Valor promedio de venta",
    granularityDaily: "Diario",
    granularityWeekly: "Semanal",
    granularityMonthly: "Mensual",
    infoTitle: "Cómo leer este gráfico",
    infoButtonLabel: "Cómo interpretarlo",
    chartNoData: "Sin datos en esta ventana.",
    series: {
      revenue: "Ingresos",
      expense: "Gastos",
      net: "Neto",
      cumRevenue: "Ingresos acumulados",
      cumExpense: "Gastos acumulados",
      cumNet: "Neto acumulado",
      rollingMargin: "Margen móvil %",
      cumulative: "Acumulado",
      newCustomers: "Clientes nuevos",
      newSuppliers: "Proveedores nuevos",
      repeatCustomers: "Clientes recurrentes",
      margin: "Margen %",
      recurring: "Recurrente",
      oneoff: "Único",
      completed: "Completados",
      overdue: "Atrasados",
      count: "Cantidad",
      total: "Total",
      units: "Unidades",
      cumShare: "Participación acumulada",
      avgDays: "Días promedio",
      conversionRate: "Tasa de conversión",
      confirmed: "Confirmado",
      paid: "Pagado",
    },
    revenueTrendChart: "Tendencia de ingresos",
    revenueShareChart: "Participación de ingresos en el tiempo",
    basketSize: "Tamaño de canasta",
    salesHeatmap: "Mapa de calor de ventas",
    receivablesAging: "Antigüedad de cuentas por cobrar",
    payablesAging: "Antigüedad de cuentas por pagar",
    salesBreakdown: "Desglose de ventas",
    expenseTrendChart: "Tendencia de gastos",
    expenseShareChart: "Participación de gastos en el tiempo",
    expenseHeatmap: "Mapa de calor de gastos",
    avgExpenseChart: "Gasto promedio",
    topSuppliers: "Principales proveedores",
    upcomingRecurring: "Próximos recurrentes",
    expenseBreakdown: {
      recurrency: "Recurrencia",
      category: "Categoría",
      supplier: "Proveedor",
    },
    paretoChart: "Curva de Pareto · ingresos por producto",
    stockBreakdownChart: "Desglose del valor de inventario",
    stockBreakdown: {
      product: "Producto",
      supplier: "Proveedor",
    },
    stockStrip: {
      outOfStock: "Sin existencias",
      belowRop: "Stock bajo",
      healthy: "Saludable",
    },
    lowStockItems: "Productos con poco stock",
    lowStockEmpty: "Todo lo monitoreado está por encima del mínimo.",
    daysOfCover: "Días de cobertura",
    turnoverByProduct: "Rotación por producto",
    daysOfCoverItems: "Artículos en esta categoría",
    moreItems: "+{{count}} más",
    coverBuckets: {
      noSales: "Sin ventas",
    },
    customerTypeSplit: "Tipos de cliente",
    ltvDistribution: "Distribución de valor de vida",
    churnProxy: "Indicador de abandono",
    cohortRetention: "Retención por cohorte",
    topByGeography: "Principales por geografía",
    topCustomersByLtv: "Mejores clientes por LTV",
    customerGrowthCumulative: "Crecimiento acumulado de clientes",
    cumulativeRevenue: "Ingresos acumulados",
    cumulativeExpenses: "Gastos acumulados",
    customerHealth: {
      active: "Activos",
      at_risk: "En riesgo",
      churned: "Perdidos",
    },
    col: {
      city: "Ciudad",
      customers: "Clientes",
      orders: "Pedidos",
      ltv: "Valor de vida",
      expenses: "Gastos",
      totalSpend: "Gasto total",
      openExpenses: "Gastos abiertos",
      balanceDue: "Saldo pendiente",
    },
    supplierGrowth: "Nuevos proveedores en el tiempo",
    spendMixChart: "Distribución de gasto",
    spendMix: {
      category: "Categoría",
      supplier: "Proveedor",
    },
    concentrationChart: "Concentración · participación del gasto",
    paymentDelayChart: "Retraso medio de pago",
    openBalancesChart: "Proveedores con saldos abiertos",
    topSuppliersLifetime: "Principales proveedores · gasto histórico",
    paymentDelayStrip: {
      onTime: "A tiempo",
      late: "Tarde",
      veryLate: "Muy tarde",
    },
    noOpenBalances: "Sin saldos pendientes por pagar.",
    turnoverByCategory: "Rotación por categoría",
    topProducts: "Productos principales",
    "salesBreakdown.product": "Producto",
    "salesBreakdown.category": "Categoría",
    "salesBreakdown.customer": "Cliente",
    "salesBreakdown.other": "Otros",
    payStrip: {
      unpaidCount: "Ventas no pagadas",
      partialCount: "Ventas parciales",
      paidCount: "Ventas pagadas",
      unpaidExpensesCount: "Gastos no pagados",
      partialExpensesCount: "Gastos parciales",
      paidExpensesCount: "Gastos pagados",
    },
    heatmap: {
      count: "Ventas",
      revenue: "Ingresos",
      partNight: "Noche",
      partMorning: "Mañana",
      partAfternoon: "Tarde",
      partEvening: "Noche temprano",
      openFullGrid: "Ver cuadrícula completa",
    },
    info: {
      cashFlow:
        "Cada período apila ingresos (entradas) y gastos (salidas) más una línea de neto. Una brecha más amplia indica mejores márgenes; un neto bajo cero es señal de consumo de caja.",
      cumulative:
        "Totales acumulados desde el inicio de la ventana seleccionada. Pendientes más pronunciadas implican acumulación más rápida; una curva que se aplana indica desaceleración.",
      pieMix:
        "El área de cada porción es su parte del total. Útil para identificar categorías dominantes y validar si conviene dividir o agrupar buckets.",
      heatmap:
        "La intensidad de cada celda es el volumen relativo para ese día de la semana y hora del día. Sirve para detectar picos y zonas muertas.",
      avgSale:
        "Tamaño promedio de ticket por período. Valores al alza suelen indicar mejor venta cruzada o mezcla con margen mayor; a la baja, descuentos o pedidos más pequeños.",
      revenueTrend:
        "Ingreso total confirmado por período. Compara las pendientes entre granularidades para separar ruido de tendencia real.",
      expenseTrend:
        "Gasto aprobado total por período. Vigila picos que no acompañen al crecimiento de ingresos.",
      revenueByCategory:
        "Ingresos divididos por categoría de producto/servicio. Ayuda a ver desplazamientos de mezcla y qué líneas mueven la aguja.",
      salesBreakdown:
        "Ingreso total agrupado por producto, categoría o cliente. Top 10 con el resto agrupado en 'Otros'.",
      revenueShare:
        "Ingreso por período apilado por los 5 principales de la dimensión elegida. La banda 'Otros' agrupa la cola larga para mantener la leyenda legible.",
      expenseShare:
        "Gasto por período apilado por recurrencia, categoría o proveedor. Incluye buckets generales para que cada dimensión sume al mismo total del período.",
      expenseByCategory:
        "Gasto aprobado dividido por categoría. Las categorías superiores merecen el mayor escrutinio.",
      basket:
        "Histograma del monto de cada venta. Una cola larga a la derecha indica que pocas ventas grandes sostienen el período; una distribución estrecha indica tickets consistentes.",
      newVsRepeat:
        "Ingresos por período divididos entre clientes nuevos y recurrentes. Una mezcla saludable mantiene una base recurrente estable que crece junto con la adquisición.",
      profitMargin:
        "Margen = (ingresos − gastos) ÷ ingresos, por período. Una línea sobre ~10 % suele ser viable; caídas sostenidas bajo cero apuntan a problemas estructurales de costos.",
      unifiedTrend:
        "Ingresos, gastos y neto por período en el eje izquierdo y el margen % en el derecho. Cambia a Acumulado para totales corridos; la línea de margen se vuelve el margen móvil (neto acumulado ÷ ingresos acumulados).",
      categoryShare:
        "Gasto apilado por categoría a lo largo del tiempo. Bandas crecientes indican que la categoría gana participación; bandas finas son estables o decrecientes.",
      aging:
        "Saldos abiertos agrupados por días vencidos. La concentración en buckets antiguos es señal de riesgo de cobro o de pago.",
      topSuppliers:
        "Proveedores ordenados por gasto aprobado. Útil para medir concentración y abrir conversaciones de consolidación.",
      topCustomers:
        "Clientes ordenados por ingreso confirmado en la ventana. Suele aplicar Pareto: pocos clientes mueven la mayoría.",
      pareto:
        "Participación acumulada de ingresos por producto descendente. La línea del 80 % es el corte clásico de Pareto: los productos a su izquierda merecen la mayor atención.",
      daysOfCover:
        "Cuánto dura el stock actual al ritmo de ventas reciente: stock actual ÷ unidades promedio vendidas por día en la ventana seleccionada. Los artículos se agrupan por ese margen — menos de 7 días requiere reorden ya; más de 90 días es sobrestock; 'no-sales' significa que no se vendió nada en la ventana. Las barras van de rojo (urgente) → azul (sobrestock).",
      turnoverByCategory:
        "Unidades vendidas ÷ unidades en stock, por categoría. Mayor = rotación más rápida; cercano a cero = movimiento lento.",
      cohort:
        "Cada fila es una cohorte adquirida en ese mes; las columnas muestran qué fracción regresó en meses siguientes. Productos sanos muestran una cola estable bien por encima de cero.",
      typeSplit:
        "Conteo de clientes por tipo. Sirve sobre todo como contexto para los demás gráficos.",
      geography:
        "Dónde viven tus clientes. Útil para evaluar profundidad de mercado y dónde concentrar el crecimiento.",
      ltvDistribution:
        "Histograma del gasto total por cliente. Una cola larga a la derecha indica cuán concentrado está el valor de vida en pocas cuentas.",
      churn:
        "Clientes agrupados por días desde su última compra. Los buckets de la derecha son los segmentos en riesgo o probablemente fugados.",
      supplierGrowth:
        "Proveedores nuevos por período. Picos suelen indicar cambios de abastecimiento; estable = base de proveedores constante.",
      categorySplitSuppliers:
        "Gasto dividido por categoría para proveedores. Revela hacia dónde van realmente los dólares de proveedores.",
      concentrationHHI:
        "Índice Herfindahl-Hirschman de concentración de gasto en proveedores (0–10000). <1500 competitivo, 1500–2500 moderado, >2500 alto — valores altos implican dependencia excesiva.",
      paymentDelay:
        "Promedio de días entre fecha de gasto y último pago, por proveedor. Demoras largas pueden tensar la relación y perder descuentos por pronto pago.",
      revenuePerEmployee:
        "Ingreso confirmado atribuido a cada miembro del equipo. Combínalo con conversión para leer esfuerzo vs resultado.",
      expensePerEmployee:
        "Gasto aprobado registrado por cada miembro del equipo. Los outliers conviene revisarlos contra la política.",
      eventsPerEmployee:
        "Eventos de calendario completados vs atrasados por persona. Útil para detectar problemas de capacidad, no como puntaje aislado de productividad.",
      teamActivity:
        "Conteo de ventas confirmadas y total de ingresos por período. Sirve para ver si más actividad se traduce en más ingresos.",
      netContribution:
        "Ingreso atribuido menos gasto registrado, por persona. Tablero financiero de primer corte; afínalo con contexto de rol antes de concluir.",
      conversion:
        "Eventos completados ÷ cantidad de ventas, por persona. Tasas más altas indican más resultado por actividad agendada.",
      revenueVsPrior:
        "Ingreso de la ventana actual vs la ventana del mismo largo justo anterior. Útil para leer momentum sin sesgo estacional.",
    },
    tabs: {
      overview: "Resumen",
      money: "Dinero",
      operations: "Operaciones",
      team: "Equipo",
      sales: "Ventas",
      expenses: "Gastos",
      finance: "Finanzas",
      inventory: "Inventario",
      customers: "Clientes",
      suppliers: "Proveedores",
      analyticsHub: "Centro de Análisis",
    },
    analyticsHub: {
      title: "Centro de Análisis",
      backToHub: "Volver al Centro de Análisis",
      cards: {
        sales: { title: "Ventas", description: "Ingresos, pedidos y estado de pago." },
        expenses: { title: "Gastos", description: "Tendencia de costos, recurrentes vs únicos, y la cascada de contribución." },
        finance: { title: "Finanzas", description: "Flujo de caja, antigüedad de cuentas, margen y mezcla por proveedor." },
        inventory: { title: "Inventario", description: "Valor del stock, rotación, curva de Pareto y artículos bajo ROP." },
        customers: { title: "Clientes", description: "Crecimiento, cohortes, RFM, LTV y mapa de actividad." },
        suppliers: { title: "Proveedores", description: "Mezcla de gasto, concentración, demora de pago y saldos abiertos." },
      },
    },
    trendsGroup: "Tendencias en el tiempo",
    trendsGroupSub: "Cada gráfica aquí sigue la granularidad seleccionada.",
    typeUnspecified: "Sin definir",
    churnNever: "Nunca",
    recurringSplitChart: "Recurrentes vs únicos",
    spendByCategory: "Gasto por categoría",
    spendComposition: "Composición del gasto",
    assigned: "Asignado",
    unassigned: "Sin asignar",
    compose: {
      byCategory: "Por categoría",
      assignedVsUnassigned: "Asignado vs Sin asignar",
      bySupplier: "Por proveedor",
    },
    team: {
      activityTrend: "Tendencia de actividad del equipo",
      netContribution: "Contribución neta por empleado",
      revenuePerEmployee: "Ingresos por empleado",
      expensesPerEmployee: "Gastos por empleado",
      eventsCompletedOverdue: "Eventos: completados vs vencidos",
      conversion: "Conversión de eventos a ventas",
      recentActivity: "Actividad reciente",
      completedEvents: "Eventos completados",
      overdueEvents: "Eventos vencidos",
      col: {
        event: "Evento",
        actor: "Actor",
        when: "Cuándo",
      },
    },
    subtitles: {
      salesBreakdown: "Mayores contribuyentes para la dimensión elegida.",
      heatmap: "Cuándo ocurren las ventas, por día de la semana.",
      basket: "Distribución de los totales de pedido.",
      typeSplit: "Mezcla de tipos de cliente.",
      ltvDistribution: "Cómo se distribuyen los clientes por valor de vida.",
      churn: "Clientes agrupados por días desde su última compra.",
      cohort: "Retención de recompra por mes de alta.",
      topCustomersByLtv: "Clientes con mayor valor de vida.",
    },
    kpis: {
      revenue: "Ingresos",
      expense: "Gastos",
      net: "Neto",
      margin: "Margen %",
      marginLabel: "margen",
      marginTrend: "Margen en el tiempo",
      vsPrior: "vs período anterior",
      equationLess: "menos",
      equationEquals: "igual a",
      netStoryEquation: "Ingresos menos gastos",
      netStoryKept: "Conservaste el {{pct}}% de los ingresos",
      netStoryLoss: "Los gastos superaron a los ingresos",
      pendingIn: "Por cobrar",
      pendingOut: "Por pagar",
      pendingNone: "Todo conciliado",
      netCompareThisPeriod: "Periodo actual",
      netComparePriorPeriod: "Periodo anterior",
      netCompareAria: "Comparativo neto: periodo actual {{current}}, periodo anterior {{previous}}",
      salesCount: "Ventas",
      avgOrderValue: "Ticket promedio",
      paidPct: "Pagado %",
      outstanding: "Por cobrar",
      total: "Total",
      count: "Cantidad",
      avg: "Promedio",
      headcount: "Personal",
      expensesTotal: "Gastos",
      expenseCount: "Cantidad de gastos",
      avgExpenseValue: "Gasto promedio",
      recurringShare: "Parte recurrente",
      netCashFlow: "Flujo de caja",
      arBalance: "Saldo CxC",
      apBalance: "Saldo CxP",
      stockValue: "Valor del stock",
      turnoverRate: "Rotación",
      lowStock: "Stock bajo",
      belowRop: "Bajo ROP",
      totalSkus: "SKUs totales",
      deadStock: "Valor de stock inmóvil",
      revenuePareto: "80% de ingresos",
      spendPareto: "80% del gasto",
      paretoSubCustomers: "de {{count}} clientes",
      paretoSubSuppliers: "de {{count}} proveedores",
      totalCustomers: "Clientes totales",
      newInPeriod: "Nuevos en periodo",
      atRisk: "En riesgo",
      avgLtv: "LTV promedio",
      totalSuppliers: "Proveedores",
      activeSuppliers90d: "Activos 90d",
      openBalance: "Saldo abierto",
      hhi: "Concentración HHI",
    },
    waterfall: {
      title: "Cascada de contribución",
      info: "Cómo se compone el total del periodo según la dimensión elegida.",
      total: "Total",
      other: "Otros",
      noData: "Sin datos de contribución en este rango.",
      bySales: {
        legend: "Agrupar por",
        product: "Producto",
        category: "Categoría",
        customer: "Cliente",
      },
      byExpenses: {
        legend: "Agrupar por",
        recurrency: "Recurrencia",
        category: "Categoría",
        supplier: "Proveedor",
        recurring: "Recurrente",
        oneoff: "Único",
      },
    },
    overviewSections: {
      topSuppliersMini: "Mejores proveedores",
      incomeFlow: "Flujo del estado de resultados",
      incomeFlowInfo:
        "P&L tipo Sankey: las fuentes de ingreso fluyen hacia el ingreso neto, que se divide en utilidad neta y gastos operativos por categoría.",
    },
    incomeFlow: {
      netRevenue: "Ingreso neto",
      netProfit: "Utilidad neta",
      operatingExpenses: "Gastos operativos",
      sourcesLegend: "Fuentes",
      sinksLegend: "Destinos",
      stageSources: "Fuentes",
      stageNetRevenue: "Ingreso neto",
      stageSplit: "División",
      stageDestinations: "Salidas",
    },
    alerts: {
      title: "Alertas",
      lowStock: "Productos con stock bajo",
      overdueAr: "Ventas por cobrar vencidas",
      overdueAp: "Gastos por pagar vencidos",
      outstanding: "pendientes",
      andMore: "+{{count}} más",
      lowStockAria: "{{count}} artículos están por debajo del mínimo de inventario",
      overdueArAria: "{{count}} ventas están vencidas y sin pagar",
      overdueApAria: "{{count}} gastos están vencidos y sin pagar",
    },
    goals: {
      activeTitle: "Metas activas",
      viewAll: "Ver todas",
      ringAria: "{{name}} está al {{pct}} por ciento del objetivo",
      prev: "Metas anteriores",
      next: "Siguientes metas",
    },
    cohortTable: {
      cohort: "Cohorte",
      size: "Tamaño",
      month1: "Mes 1",
      month3: "Mes 3",
      month6: "Mes 6",
      month12: "Mes 12",
      empty: "Aún no hay suficiente historial para construir cohortes.",
    },
    empty: {
      title: "Vamos a configurar tu negocio",
      subtitle: "Empieza por donde quieras — puedes hacerlos en cualquier orden y tu dashboard se llenará a medida que avances.",
      addCustomerTitle: "Agrega tu primer cliente",
      addCustomerBody: "Guarda a quién le vendes para construir historial y generar reportes.",
      addProductTitle: "Agrega un producto o servicio",
      addProductBody: "Registra lo que vendes — los productos siguen el inventario automáticamente.",
      recordSaleTitle: "Registra tu primera venta",
      recordSaleBody: "Con una sola venta el dashboard cobra vida y comprueba el flujo.",
    },
  },
  customers: {
    title: "Clientes",
    countTotal: "{{count}} en total",
    add: "Agregar cliente",
    searchPlaceholder: "Buscar por nombre, correo o teléfono…",
    allActivity: "Toda actividad",
    name: "Nombre",
    email: "Correo",
    phone: "Teléfono",
    address: "Dirección",
    notes: "Notas",
    noCustomers: "Sin clientes aún. Agrega el primero.",
    deleteTitle: "Eliminar cliente",
    deleteMessage: "¿Eliminar a {{name}}? Esta acción no se puede deshacer.",
    created: "Cliente creado.",
    updated: "Cliente actualizado.",
    deleted: "Cliente eliminado.",
    failedCreate: "Error al crear cliente.",
    newCustomer: "Nuevo cliente",
    editCustomer: "Editar cliente",
    createCustomer: "Crear cliente",
    saveChanges: "Guardar cambios",
    back: "Clientes",
    totalCustomers: "Total de clientes",
    newThisMonth: "Nuevos este mes",
    withEmail: "Con correo",
    withPhone: "Con teléfono",
    repeat: "Recurrentes",
    topRevenue80pct: "80% de ingresos",
    topRevenue80pctSub: "Menor número de clientes que explica el 80% de ingresos",
    fields: {
      name: "Nombre",
      email: "Correo",
      phone: "Teléfono",
      address: "Dirección",
      notes: "Notas",
      date_of_birth: "Fecha de nacimiento",
      city: "Ciudad",
      state: "Estado / Provincia",
      country: "País",
      customer_type: "Tipo de cliente",
    },
    customerType: {
      individual: "Individual",
      company: "Empresa",
    },
    salesHistory: "Historial de ventas",
    contactInfo: "Datos de contacto",
    backToCustomers: "Volver a clientes",
    notFound: "Cliente no encontrado.",
    noNotes: "Sin notas.",
    noSales: "Aún no hay ventas para este cliente.",
    deleteMessageWithSales: "¿Eliminar a {{name}}? Las ventas asociadas perderán el enlace al cliente.",
    kpis: {
      lifetimeValue: "Valor de por vida",
      lifetimeValueSub: "Ventas confirmadas",
      lastPurchase: "Última compra",
      lastPurchaseSubNone: "Aún sin compras",
      outstandingBalance: "Saldo pendiente",
      outstandingBalanceSubAllPaid: "Todo pagado",
      outstandingBalanceSubOwed: "Adeudo en ventas confirmadas",
    },
  },
  suppliers: {
    title: "Proveedores",
    countTotal: "{{count}} en total",
    add: "Agregar proveedor",
    searchPlaceholder: "Buscar por nombre, correo o contacto…",
    name: "Nombre",
    email: "Correo",
    phone: "Teléfono",
    address: "Dirección",
    contactPerson: "Contacto",
    contactInfo: "Datos de contacto",
    notes: "Notas",
    noNotes: "Sin notas.",
    noSuppliers: "Sin proveedores aún. Agrega el primero.",
    noExpenses: "Aún no hay gastos vinculados a este proveedor.",
    deleteTitle: "Eliminar proveedor",
    deleteMessage: "¿Eliminar a {{name}}? Esta acción no se puede deshacer.",
    created: "Proveedor creado.",
    updated: "Proveedor actualizado.",
    deleted: "Proveedor eliminado.",
    failedCreate: "Error al crear proveedor.",
    newSupplier: "Nuevo proveedor",
    editSupplier: "Editar proveedor",
    createSupplier: "Crear proveedor",
    saveChanges: "Guardar cambios",
    back: "Proveedores",
    backToSuppliers: "Volver a proveedores",
    notFound: "Proveedor no encontrado.",
    totalSuppliers: "Total de proveedores",
    newThisMonth: "Nuevos este mes",
    withEmail: "Con correo",
    withPhone: "Con teléfono",
    active90d: "Activos 90d",
    active90dSub: "Con actividad en los últimos 90 días",
    openBalance: "Saldo abierto",
    openBalanceSub: "Saldo pendiente con proveedores",
    topSpend80pct: "80% del gasto",
    topSpend80pctSub: "Menor número de proveedores que explica el 80% del gasto",
    expenseHistory: "Historial de gastos",
    kpis: {
      lifetimeValue: "Gasto total",
      lifetimeValueSub: "Gastos de por vida",
      lastPurchase: "Última compra",
      lastPurchaseSubNone: "Aún sin gastos",
      outstandingBalance: "Saldo pendiente",
      outstandingBalanceSubAllPaid: "Todo pagado",
      outstandingBalanceSubOwed: "Adeudo en gastos",
    },
  },
  sales: {
    title: "Ventas",
    countTotal: "{{count}} en total",
    new: "Nueva venta",
    confirmedCount: "Confirmadas en el periodo",
    searchPlaceholder: "Buscar ventas…",
    allStatuses: "Todos los estados",
    allPayStatuses: "Todos los estados de pago",
    noSales: "Sin ventas aún.",
    deleteTitle: "Eliminar venta",
    deleteMessage: "¿Eliminar esta venta? Esta acción no se puede deshacer.",
    titleField: "Título",
    idField: "ID de venta",
    customer: "Cliente",
    noCustomer: "Sin cliente",
    walkIn: "Sin cliente",
    status: "Estado",
    date: "Fecha de venta",
    total: "Total",
    items: "Artículos",
    notes: "Notas",
    created: "Venta creada.",
    deleted: "Venta eliminada.",
    updated: "Venta actualizada.",
    totalRevenue: "Ingresos totales",
    confirmedSales: "Ventas confirmadas",
    avgSaleValue: "Valor promedio",
    confirmedRevenue: "Ingresos confirmados",
    avgValue: "Promedio",
    outstanding: "Por cobrar",
    outstandingSub: "Saldo pendiente en ventas confirmadas",
    newSale: "Nueva venta",
    editSale: "Editar venta",
    createSale: "Crear venta",
    saveChanges: "Guardar cambios",
    addItem: "+ Agregar artículo",
    description: "Producto / servicio",
    quantity: "Cant.",
    unitPrice: "$ Unit.",
    grandTotal: "Total",
    back: "Ventas",
    itemDescription: "Producto / servicio",
    qty: "Cant.",
    inventoryStockOnConfirm: "El stock se descontará cuando se confirme esta venta.",
    payStatus: "Estado de pago",
    markedPaid: "Venta marcada como pagada.",
    paymentsCancelled: "Pagos de la venta cancelados.",
    cancelPaymentsTitle: "¿Cancelar los pagos de la venta?",
    cancelPaymentsMessage: "Se eliminarán todos los pagos de esta venta y volverá al estado sin pagar. Esta acción no se puede deshacer.",
    cancelPaymentsConfirm: "Cancelar pagos",
    exportReceipt: "Exportar recibo",
    receiptExportFailed: "No se pudo exportar el recibo.",
  },
  expenses: {
    title: "Gastos",
    countTotal: "{{count}} en total",
    new: "Nuevo gasto",
    searchPlaceholder: "Buscar gastos…",
    allCategories: "Todas las categorías",
    allPayStatuses: "Todos los estados de pago",
    noExpenses: "Sin gastos aún.",
    deleteTitle: "Eliminar gasto",
    deleteMessage: "¿Eliminar este gasto? Esta acción no se puede deshacer.",
    titleField: "Título",
    idField: "ID de gasto",
    amount: "Monto",
    category: "Categoría",
    date: "Fecha de gasto",
    notes: "Notas",
    created: "Gasto creado.",
    deleted: "Gasto eliminado.",
    updated: "Gasto actualizado.",
    totalRecorded: "Total registrado",
    recordedCount: "Gastos registrados",
    recordedExpenses: "Gastos registrados",
    recordedThisPeriod: "Registrados en el período",
    topCategory: "Categoría principal",
    avgExpense: "Gasto promedio",
    avgExpenseSub: "Promedio por gasto registrado",
    outstanding: "Por pagar",
    outstandingSub: "Saldo pendiente en gastos",
    newExpense: "Nuevo gasto",
    editExpense: "Editar gasto",
    createExpense: "Crear gasto",
    saveChanges: "Guardar cambios",
    back: "Gastos",
    catOffice: "Oficina",
    catTravel: "Viajes",
    catMeals: "Comidas",
    catSoftware: "Software",
    catEquipment: "Equipamiento",
    catMarketing: "Marketing",
    catOther: "Otro",
    inventoryStockOnCreate: "El stock se sumará al registrar este gasto.",
    payStatus: "Estado de pago",
    markedPaid: "Gasto marcado como pagado.",
    paymentsCancelled: "Pagos del gasto cancelados.",
    cancelPaymentsTitle: "¿Cancelar los pagos del gasto?",
    cancelPaymentsMessage: "Se eliminarán todos los pagos de este gasto y volverá al estado sin pagar. Esta acción no se puede deshacer.",
    cancelPaymentsConfirm: "Cancelar pagos",
    recurringSection: "Recurrencia",
    isRecurring: "Hacer este gasto recurrente",
    recurrenceFrequency: "Frecuencia",
    recurrenceEvery: "Cada",
    recurrenceInterval: "Intervalo",
    recurrencePeriod: "Período",
    periodDays: "Días",
    periodWeeks: "Semanas",
    periodMonths: "Meses",
    periodYears: "Años",
    recurrenceEndType: "Termina",
    recurrenceEndCount: "¿Después de cuántas ocurrencias?",
    recurrenceEndDate: "Fecha de término",
    recurringBadge: "Recurrente",
    endNever: "Nunca",
    endCount: "Después de N ocurrencias",
    endDate: "En una fecha específica",
    supplier: "Proveedor",
    noSupplier: "Sin proveedor",
    receipt: "Recibo",
    receiptAttach: "Adjuntar recibo",
    receiptReplace: "Reemplazar recibo",
    receiptOpen: "Abrir",
    receiptHint: "JPEG, PNG, GIF, WEBP, HEIC o PDF",
    receiptWillUploadOnSave: "{{name}} se subirá al guardar.",
    receiptUploaded: "Recibo subido.",
    receiptError: "No se pudo subir el recibo.",
    receiptPdf: "Recibo PDF",
  },
  catalog: {
    title: "Catálogo",
    subtitle: "Tu portafolio de productos y servicios.",
    countItems: "{{count}} artículos",
    new: "Nuevo artículo",
    searchPlaceholder: "Buscar en catálogo…",
    allItems: "Todos",
    activeOnly: "Solo activos",
    inactiveOnly: "Solo inactivos",
    allCategories: "Todas las categorías",
    noItems: "Sin artículos. Agrega tu primer producto o servicio.",
    deleteTitle: "Eliminar artículo",
    deleteMessage: "¿Eliminar \"{{name}}\"? Esta acción no se puede deshacer.",
    name: "Nombre",
    description: "Descripción",
    price: "Precio",
    unit: "Unidad",
    category: "Categoría",
    status: "Estado",
    created: "Artículo creado.",
    deleted: "Artículo eliminado.",
    updated: "Artículo actualizado.",
    totalItems: "Total de artículos",
    activeItems: "Artículos activos",
    catalogValue: "Valor del catálogo activo",
    catalogValueSub: "Suma de precios activos",
    newItem: "Nuevo artículo",
    editItem: "Editar artículo",
    createItem: "Crear artículo",
    saveChanges: "Guardar cambios",
    namePlaceholder: "Diseño web",
    activeLabel: "Activo (visible en ventas)",
    perUnit: "por {{unit}}",
    addedBy: "Agregado por",
    createdAt: "Creado",
    back: "Catálogo",
    unitUnit: "Unidad",
    unitHour: "Hora",
    unitDay: "Día",
    unitMonth: "Mes",
    unitLicense: "Licencia",
    unitOther: "Otro",
    catProduct: "Producto",
    catService: "Servicio",
    catDigital: "Digital",
    catSubscription: "Suscripción",
    catBundle: "Paquete",
    catConsulting: "Consultoría",
    catOther: "Otro",
    inventoryTitle: "Inventario",
    inventoryStock: "Stock",
    inventoryMinStock: "Stock mínimo",
    inventoryView: "Ver inventario →",
    lastUpdate: "Última actualización",
  },
  inventory: {
    title: "Inventario",
    subtitle: "Controla el stock de tus productos.",
    addItem: "Agregar artículo",
    newItem: "Nuevo artículo de inventario",
    editItem: "Editar artículo de inventario",
    saveChanges: "Guardar cambios",
    createItem: "Crear artículo",
    catalogItem: "Producto",
    selectProduct: "Selecciona un producto…",
    noProducts: "No hay productos activos. Agrega un producto al catálogo primero.",
    quantity: "Stock actual",
    minQuantity: "Stock mínimo",
    minQuantityHint: "Alerta cuando esté por debajo (0 = sin alerta)",
    searchPlaceholder: "Buscar productos…",
    filterLowStock: "Solo stock bajo",
    allStockStatuses: "Todos los estados",
    stockStatus: {
      outOfStock: "Sin existencias",
      belowRop: "Bajo el ROP",
      healthy: "Saludable",
    },
    noItems: "Sin artículos en inventario aún.",
    colProduct: "Producto",
    colUnit: "Unidad",
    colQuantity: "Stock",
    colMinQty: "Mín.",
    colUpdated: "Última actualización",
    colStatus: "Estado",
    colActions: "Acciones",
    statusOk: "En stock",
    statusLowStock: "Stock bajo",
    statusOutOfStock: "Sin existencias",
    adjust: "Ajustar",
    viewMovements: "Ver historial de movimientos",
    movementsTitle: "Historial de movimientos",
    noMovements: "Sin movimientos registrados aún.",
    fromExpense: "Gasto",
    fromSale: "Venta",
    movTypeIn: "Entrada",
    movTypeOut: "Salida",
    movTypeAdjustment: "Ajuste",
    adjustTitle: "Ajustar stock",
    currentStock: "Stock actual",
    adjustDelta: "Cambio de cantidad",
    adjustDeltaHint: "Positivo para agregar stock, negativo para retirar.",
    adjustNotes: "Notas",
    applyAdjustment: "Aplicar",
    adjusted: "Stock ajustado correctamente.",
    created: "Artículo de inventario creado.",
    updated: "Artículo de inventario actualizado.",
    deleted: "Artículo de inventario eliminado.",
    deleteConfirmTitle: "¿Eliminar artículo de inventario?",
    deleteConfirmMessage: "Se eliminará el registro de inventario para \"{{name}}\" y su historial de movimientos.",
    kpiTotalItems: "Artículos rastreados",
    kpiTotalItemsSub: "Productos con inventario",
    kpiLowStock: "Stock bajo",
    kpiLowStockSub: "Por debajo del mínimo",
    kpiLowStockAria: "Filtrar por artículos con stock bajo",
    kpiInStock: "En stock",
    kpiInStockAria: "Filtrar por artículos en stock (saludable)",
    kpiOutOfStock: "Sin stock",
    kpiOutOfStockAria: "Filtrar por artículos sin stock",
    kpiStockValue: "Valor del stock",
    kpiStockValueSub: "Cantidad × precio catálogo",
    inventoryItem: "Artículo de inventario",
    inventoryQtyReceived: "Cant. recibida",
    inventoryItemHint: "Vincula a un inventario para rastrear el stock",
    noInventoryItems: "Sin artículos de inventario disponibles",
    notFound: "Artículo de inventario no encontrado.",
    backToList: "Volver al inventario",
    productInfo: "Información del producto",
    category: "Categoría",
    timestamps: "Fechas",
    createdAt: "Creado",
    updatedAt: "Última actualización",
    colType: "Tipo",
    colChange: "Cambio",
    colSource: "Origen",
    colNotes: "Notas",
    colBy: "Por",
    colDate: "Fecha",
  },
  categories: {
    manage: "Gestionar categorías",
    catalogTitle: "Categorías del catálogo",
    expenseTitle: "Categorías de gastos",
    systemCategory: "Categoría del sistema — no se puede modificar",
    newPlaceholder: "Nombre de nueva categoría…",
    add: "Agregar",
    confirmDelete: "¿Eliminar esta categoría?",
    /** Etiquetas para categorías de sistema usadas por `formatCategoryLabel`. */
    expense: {
      office: "Oficina",
      travel: "Viajes",
      meals: "Comidas",
      software: "Software",
      equipment: "Equipos",
      marketing: "Marketing",
      other: "Otros",
    },
    catalog: {
      product: "Producto",
      service: "Servicio",
      digital: "Digital",
      subscription: "Suscripción",
      bundle: "Paquete",
      consulting: "Consultoría",
      other: "Otros",
    },
  },
  employees: {
    title: "Empleados",
    subtitle: "Gestiona tu equipo y sus roles.",
    totalEmployees: "Total empleados",
    invite: "Invitar empleado",
    name: "Nombre",
    email: "Correo",
    role: "Rol",
    status: "Estado",
    actions: "Acciones",
    edit: "Editar",
    deactivate: "Desactivar",
    noEmployees: "Sin empleados. Invita a alguien para comenzar.",
    deactivateTitle: "¿Desactivar empleado?",
    deactivateConfirm: "¿Desactivar a {{name}}?",
    deactivateMessage: "¿Desactivar a {{name}}? No podrá iniciar sesión hasta que sea reactivado. Las ventas y gastos que registró seguirán mostrando su nombre.",
    deactivated: "Empleado desactivado.",
    reactivate: "Reactivar",
    reactivateTitle: "¿Reactivar empleado?",
    reactivateMessage: "¿Reactivar a {{name}}? Recuperará el acceso para iniciar sesión inmediatamente.",
    reactivated: "Empleado reactivado.",
    roleUpdated: "Rol actualizado.",
    admin: "Administrador",
    manager: "Gerente",
    salesRep: "Vendedor",
    ofTotal: "de {{total}} en total",
    editTitle: "Editar empleado",
    firstName: "Nombre",
    lastName: "Apellido",
    adminOnly: "La gestión del equipo solo está disponible para administradores del negocio. Pide a un administrador si necesitas un cambio aquí.",
    pendingInvites: "Invitaciones pendientes",
    resendInvite: "Reenviar invitación",
    revokeInvite: "Revocar invitación",
    revokeTitle: "¿Revocar invitación?",
    revokeMessage: "¿Revocar la invitación para {{email}}? Su enlace dejará de funcionar de inmediato.",
    expiresOn: "Expira {{date}}",
    inviteSeatLimit: "Tu plan permite hasta {{max}} miembros del equipo. Elimina a un miembro o mejora tu plan para invitar a más.",
    inviteEmailFailed: "La invitación se creó pero el correo no pudo enviarse. Usa Reenviar en la lista de invitaciones pendientes.",
    inviteModal: {
      title: "Invitar empleado",
      subtitle: "Envía un correo de invitación para agregar a un nuevo miembro del equipo.",
      email: "Correo electrónico",
      role: "Rol",
      loadingRoles: "Cargando roles…",
      send: "Enviar invitación",
      sending: "Enviando…",
      failed: "No se pudo enviar la invitación.",
    },
  },
  businessSetup: {
    title: "Configura tu negocio",
    subtitle: "Pongamos tu espacio listo en unos pasos rápidos.",
    stepBasics: "Básicos",
    stepDesign: "Diseño",
    stepTeam: "Equipo",
    skipForNow: "Omitir por ahora",
    skipInvites: "Continuar sin invitar",
    finishCta: "Finalizar y enviar invitaciones",
    addInvite: "Agregar otro",
    inviteSubtitle: "Invita a compañeros por correo. Recibirán un enlace para unirse a tu negocio.",
    inviteEmpty: "Aún no hay invitaciones. Haz clic en \"Agregar otro\" para invitar, u omite este paso.",
    inviteEmailPlaceholder: "compañero@ejemplo.com",
    inviteSent: "Invitación enviada",
    inviteSomeFailed: "Algunas invitaciones no se pudieron enviar. Revisa y reintenta.",
    createError: "No se pudo crear el negocio.",
    finished: "¡Negocio listo!",
    finishedWithInvites: "¡Negocio listo! Se enviaron {{count}} invitación(es).",
  },
  account: {
    title: "Mi cuenta",
    subtitle: "Gestiona tu perfil y preferencias.",
    profileSection: "Perfil",
    preferencesSection: "Preferencias",
    firstName: "Nombre",
    lastName: "Apellido",
    email: "Correo electrónico",
    emailNote: "Contacta a soporte para cambiar tu correo.",
    language: "Idioma de la interfaz",
    languageSubtitle: "Elige el idioma para toda la aplicación.",
    langSaved: "Idioma actualizado.",
    appearance: "Apariencia",
    appearanceSubtitle: "Cambia entre el tema claro y oscuro.",
    themeLight: "Claro",
    themeDark: "Oscuro",
    themeSaved: "Tema actualizado.",
    switchToDark: "Cambiar a modo oscuro",
    switchToLight: "Cambiar a modo claro",
    appDesign: "Diseño de la app",
    appDesignSubtitle: "Estos ajustes solo afectan cómo se ve la app para ti.",
    profilePicture: "Foto de perfil",
    profilePictureHint: "Haz clic en el ícono de cámara para subir una nueva foto.",
    avatarSaved: "Foto de perfil actualizada.",
    avatarError: "Error al subir la foto de perfil.",
    security: "Seguridad",
    securitySubtitle: "Actualiza la contraseña que usas para iniciar sesión.",
    password: "Contraseña",
    changePassword: "Cambiar contraseña",
    changePasswordSubtitle: "Ingresa tu contraseña actual y elige una nueva.",
    currentPassword: "Contraseña actual",
    newPassword: "Nueva contraseña",
    confirmNewPassword: "Confirmar nueva contraseña",
    passwordHint: "Al menos 8 caracteres.",
    currentPasswordRequired: "Ingresa tu contraseña actual.",
    passwordTooShort: "La contraseña debe tener al menos 8 caracteres.",
    passwordsDoNotMatch: "Las contraseñas no coinciden.",
    passwordChanged: "Contraseña actualizada.",
    changePasswordError: "No se pudo actualizar tu contraseña. Inténtalo de nuevo.",
    updating: "Actualizando…",
  },
  features: {
    inventory_enabled: {
      label: "Inventario",
      description: "Lleva control de existencias y alertas de stock bajo.",
    },
    suppliers_enabled: {
      label: "Proveedores",
      description: "Gestiona proveedores, términos de pago y antigüedad de cuentas por pagar.",
    },
    employees_enabled: {
      label: "Equipo",
      description: "Registra empleados y su desempeño individual.",
    },
    calendar_enabled: {
      label: "Calendario",
      description: "Agenda citas y eventos.",
    },
    catalog_enabled: {
      label: "Catálogo",
      description: "Define productos y servicios para tus ventas.",
    },
    section: {
      title: "Funciones",
      subtitle: "Oculta las pestañas que tu negocio no usa. Tus datos se conservan al desactivar.",
      toggleHeader: "Funciones opcionales",
      toggleSubtitle: "Solo dueño/admin — estos interruptores afectan a todo el equipo.",
    },
    defaultOffBadge: "Desactivado por defecto para {{type}}",
    confirmDisable: {
      title: "¿Ocultar {{feature}}?",
      message: "Tus datos se conservan — puedes reactivarlo cuando quieras. La pestaña y la fila de navegación desaparecerán de inmediato.",
      confirmLabel: "Ocultar",
    },
    disabledPage: {
      title: "{{feature}} está desactivado",
      description: "Tu negocio tiene esta función desactivada. Reactívala en Configuración → Negocio → Funciones.",
      cta: "Activar en Configuración",
    },
  },
  settings: {
    logoAlt: "Logo del negocio",
    changePreset: "Cambiar plantilla",
    noVisibleFields: "(No hay campos visibles para este elemento)",
    computingChanges: "Calculando cambios…",
    removeSelection: "Quitar {{preset}}",
    dragToReorder: "Arrastra para reordenar",
    presetDiffFieldsShown: "Campos mostrados",
    presetDiffFieldsHidden: "Campos ocultados",
    presetDiffRequiredToggles: "Cambios en campos obligatorios",
    presetDiffNewCustom: "Nuevos campos personalizados",
    presetDiffWipedCustom: "Campos personalizados eliminados",
    consultingTitle: "Servicios de consultoría",
    consultingSubtitle: "Asesoría experta de nuestro equipo de especialistas en negocios",
    consultingContactCta: "Escríbenos",
    consultingScheduleNote: "Cuéntanos sobre tu negocio y te responderemos.",
    consultingMailSubject: "Consulta sobre consultoría",
    consultingMailBody:
      "Hola equipo de Innbuilder,\n\nMe gustaría conocer más sobre sus servicios de consultoría para mi negocio.\n",
    consultingBizDev: "Desarrollo de negocio",
    consultingStrategy: "Estrategia y planeación",
    consultingForecasting: "Pronóstico de ingresos",
    consultingDigital: "Transformación digital",
    planCustomPricing: "Precio personalizado",
    planPerMonth: "/mes",
    planCurrentPlan: "Plan actual",
    planMonthsFree: "{{count}} meses GRATIS",
    planUnlimitedUsers: "Usuarios ilimitados",
    planOneUserSeat: "1 lugar de usuario",
    planUpToUsers: "Hasta {{count}} usuarios (ampliable)",
    planContactSales: "Contactar a ventas",
    planRedirecting: "Redirigiendo…",
    planSelectPlan: "Elegir plan",
    packUsersCount: "{{count}} usuarios",
    packPriceBreakdown: "Desglose de precio",
    packBaseRow: "Base ({{count}} usuarios): ${{price}}/mes",
    packRow: "+{{users}} usuarios (paquete {{pack}}): +${{cost}}/mes",
    packTotalRow: "Total: ${{amount}}/mes",
    title: "Configuración del negocio",
    subtitle: "Gestiona la información y marca de tu negocio.",
    adminOnly: "Solo los administradores del negocio pueden editar esta configuración.",
    generalSection: "Información general",
    contactSection: "Contacto y presencia en línea",
    identitySection: "Identidad del negocio",
    name: "Nombre del negocio",
    phone: "Teléfono",
    address: "Dirección",
    description: "Descripción",
    descriptionPlaceholder: "Breve descripción de tu negocio…",
    website: "Sitio web",
    email: "Correo del negocio",
    industry: "Industria",
    logo: "Logotipo del negocio",
    logoHint: "Haz clic en el ícono de cámara para subir un logotipo.",
    logoSaved: "Logotipo actualizado.",
    logoError: "Error al subir el logotipo.",
    taxId: "RFC / ID fiscal",
    saved: "Configuración actualizada.",
    selectIndustry: "Seleccionar industria…",
    industryTechnology: "Tecnología",
    industryRetail: "Comercio",
    industryFoodBeverage: "Alimentos y bebidas",
    industryHealthcare: "Salud",
    industryEducation: "Educación",
    industryFinance: "Finanzas",
    industryConstruction: "Construcción",
    industryConsulting: "Consultoría",
    industryMarketing: "Marketing",
    industryLogistics: "Logística",
    industryRealEstate: "Bienes raíces",
    industryOther: "Otro",
    generalTab: "General",
    accessTab: "Control de acceso",
    subscriptionTab: "Suscripción",
    formFieldsTab: "Campos de formulario",
    design: {
      subtitle: "Personaliza cómo se ve la app para todos en tu empresa.",
      themeSection: "Tema",
      themeHint: "Modo claro u oscuro de la interfaz.",
      colorSection: "Color Primario",
      colorHint: "Usado para botones, enlaces y acentos en toda la app.",
      colorViolet: "Violeta",
      colorBlue: "Azul metálico",
      colorGold: "Dorado",
      colorSilver: "Plateado",
      fontSection: "Tipografía",
      fontHint: "La fuente utilizada en toda la app.",
      fontSansation: "Sansation",
      fontInter: "Inter",
      fontRoboto: "Roboto",
      fontPoppins: "Poppins",
      fontSystem: "Sistema",
      reset: "Restablecer",
    },
    formFields: {
      entityLabel: "Entidad:",
      searchPlaceholder: "Buscar campos…",
      noMatches: "Sin coincidencias.",
      entity: {
        customer: "Clientes",
        supplier: "Proveedores",
        expense: "Gastos",
        catalog: "Catálogo",
        inventory: "Inventario",
        employee: "Empleados",
        sale: "Ventas",
      },
      builtinTitle: "Campos estándar",
      builtinSubtitle: "Controla qué campos estándar aparecen en el formulario.",
      customTitle: "Campos personalizados",
      customSubtitle: "Agrega tus propios campos para recopilar información adicional.",
      visible: "Visible",
      required: "Obligatorio",
      alwaysRequired: "(siempre obligatorio)",
      hidden: "oculto",
      addField: "Agregar campo",
      newField: "Nuevo campo personalizado",
      fieldLabel: "Etiqueta",
      fieldLabelPlaceholder: "ej. Número de membresía",
      fieldType: "Tipo",
      fieldOptions: "Opciones (separadas por coma)",
      fieldOptionsPlaceholder: "ej. Bronce, Plata, Oro",
      saveField: "Crear campo",
      saved: "Configuración guardada.",
      saveError: "Error al guardar. Inténtalo de nuevo.",
      fieldCreated: "Campo personalizado creado.",
      fieldDeleted: "Campo personalizado eliminado.",
      deleteFieldConfirm: "¿Eliminar \"{{label}}\"? Se eliminarán sus valores de todos los registros.",
      noCustomFields: "Sin campos personalizados aún. Agrega uno arriba.",
      fieldTypes: {
        text: "Texto",
        textarea: "Texto multilínea",
        number: "Número",
        date: "Fecha",
        select: "Lista desplegable",
        email: "Correo",
        phone: "Teléfono",
        checkbox: "Casilla",
        file: "Archivo",
      },
      field: {
        customer: {
          name: "Nombre",
          email: "Correo",
          phone: "Teléfono",
          address: "Dirección",
          notes: "Notas",
          date_of_birth: "Fecha de nacimiento",
          city: "Ciudad",
          state: "Estado / Provincia",
          country: "País",
          customer_type: "Tipo de cliente",
          company: "Empresa",
          tax_id: "RFC / NIF",
          website: "Sitio web",
          language_preference: "Idioma",
          referral_source: "Fuente de referencia",
          marketing_opt_in: "Acepta marketing",
          preferred_contact_method: "Contacto preferido",
          loyalty_id: "ID de lealtad",
          vip_tier: "Nivel VIP",
          allergies: "Alergias",
          insurance_id: "ID de seguro",
          license_plate: "Matrícula",
          project_ref: "Proyecto",
        },
        supplier: {
          name: "Nombre",
          email: "Correo",
          phone: "Teléfono",
          address: "Dirección",
          contact_person: "Persona de contacto",
          notes: "Notas",
          website: "Sitio web",
          tax_id: "RFC / NIF",
          payment_terms: "Términos de pago",
          lead_time_days: "Tiempo de entrega (días)",
          preferred_currency: "Moneda preferida",
          bank_account: "Cuenta bancaria",
          iban: "IBAN",
          swift: "SWIFT",
          minimum_order: "Pedido mínimo",
          discount_tier: "Nivel de descuento",
          category: "Categoría",
          rating: "Calificación",
          certifications: "Certificaciones",
          on_hold: "En pausa",
        },
        expense: {
          title: "Título",
          amount: "Monto",
          category: "Categoría",
          expense_date: "Fecha",
          supplier: "Proveedor",
          notes: "Notas",
          payment_method: "Método de pago",
          reference_number: "Referencia",
          tax_amount: "Impuesto",
          tax_rate: "Tasa de impuesto",
          billable: "Facturable",
          project_ref: "Proyecto",
          approved_by: "Aprobado por",
          reimbursable: "Reembolsable",
          receipt: "Recibo",
          currency: "Moneda",
          recurring: "Recurrente",
          department: "Departamento",
        },
        catalog: {
          name: "Nombre",
          description: "Descripción",
          category: "Categoría",
          price: "Precio",
          unit: "Unidad",
          is_active: "Activo",
          sku: "SKU",
          barcode: "Código de barras",
          brand: "Marca",
          supplier_default: "Proveedor predeterminado",
          cost_price: "Costo",
          margin: "Margen",
          weight: "Peso",
          dimensions: "Dimensiones",
          tax_class: "Clase fiscal",
          warranty_months: "Garantía (meses)",
          serial_tracked: "Rastreo por serie",
          hazard_class: "Clase de peligro",
          shelf_life_days: "Vida útil (días)",
          allergens: "Alérgenos",
          min_order_qty: "Cantidad mínima",
        },
        inventory: {
          quantity: "Cantidad",
          min_quantity: "Cantidad mínima",
          location: "Ubicación",
          lot_number: "Lote",
          serial_number: "Número de serie",
          expiry_date: "Vencimiento",
          last_restocked_at: "Último reabasto",
          reorder_qty: "Cantidad de reorden",
          reserved_qty: "Cantidad reservada",
          max_quantity: "Cantidad máxima",
          cost_layer: "Capa de costo",
          supplier_ref: "Referencia de proveedor",
        },
        employee: {
          role: "Rol",
          is_active: "Activo",
          employee_code: "Código de empleado",
          phone: "Teléfono",
          address: "Dirección",
          hire_date: "Fecha de contratación",
          date_of_birth: "Fecha de nacimiento",
          department: "Departamento",
          emergency_contact_name: "Contacto de emergencia",
          emergency_contact_phone: "Teléfono de emergencia",
          id_number: "Identificación",
          tax_id: "RFC / NIF",
          bank_account: "Cuenta bancaria",
          salary: "Salario",
          pay_frequency: "Frecuencia de pago",
          employment_type: "Tipo de empleo",
          status: "Estatus",
          termination_date: "Fecha de baja",
          certifications: "Certificaciones",
          shirt_size: "Talla de camisa",
        },
        sale: {
          status: "Estatus",
          pay_status: "Estado de pago",
          sale_date: "Fecha de venta",
          notes: "Notas",
          payment_method: "Método de pago",
          reference_number: "Referencia",
          channel: "Canal",
          sales_rep: "Vendedor",
          project_ref: "Proyecto",
          po_number: "Número de OC",
          shipping_method: "Método de envío",
          delivery_date: "Fecha de entrega",
          invoice_number: "Número de factura",
          currency: "Moneda",
          notes_internal: "Notas internas",
        },
      },
      option: {
        customer: {
          customer_type: { individual: "Individual", company: "Empresa" },
          preferred_contact_method: { email: "Correo", phone: "Teléfono", sms: "SMS" },
          vip_tier: { bronze: "Bronce", silver: "Plata", gold: "Oro", platinum: "Platino" },
        },
        supplier: {
          payment_terms: { net_15: "Neto 15", net_30: "Neto 30", net_60: "Neto 60", net_90: "Neto 90", cod: "Contra entrega", prepaid: "Prepago" },
          discount_tier: { a: "Nivel A", b: "Nivel B", c: "Nivel C", none: "Ninguno" },
        },
        expense: {
          payment_method: { cash: "Efectivo", card: "Tarjeta", transfer: "Transferencia", check: "Cheque", other: "Otro" },
        },
        catalog: {
          unit: { unit: "Unidad", hour: "Hora", day: "Día", month: "Mes", license: "Licencia", other: "Otro" },
        },
        inventory: {
          cost_layer: { fifo: "FIFO", lifo: "LIFO", average: "Promedio" },
        },
        employee: {
          pay_frequency: { hourly: "Por hora", weekly: "Semanal", biweekly: "Quincenal", monthly: "Mensual" },
          employment_type: { full_time: "Tiempo completo", part_time: "Medio tiempo", contract: "Contrato", intern: "Pasante" },
          status: { active: "Activo", on_leave: "De licencia", terminated: "Terminado" },
          shirt_size: { xs: "XS", s: "S", m: "M", l: "L", xl: "XL", xxl: "XXL" },
        },
        sale: {
          status: { draft: "Borrador", confirmed: "Confirmada", cancelled: "Cancelada" },
          pay_status: { unpaid: "Sin pagar", partial: "Parcial", paid: "Pagado" },
          payment_method: { cash: "Efectivo", card: "Tarjeta", transfer: "Transferencia", check: "Cheque", other: "Otro" },
          channel: { in_store: "En tienda", online: "En línea", phone: "Teléfono" },
        },
      },
    },
    accessControlSubtitle: "Gestiona qué puede ver y hacer cada rol en cada sección.",
    rolesTitle: "Roles",
    addRole: "Agregar rol",
    roleNamePlaceholder: "Nombre del rol…",
    deleteRole: "Eliminar rol",
    deleteRoleConfirm: "¿Eliminar el rol \"{{name}}\"? Los empleados con este rol deberán ser reasignados.",
    systemRole: "Sistema",
    customRole: "Personalizado",
    permView: "Ver",
    permEdit: "Editar",
    permNone: "Ninguno",
    saveRolePermissions: "Guardar",
    permissionsSaved: "Permisos guardados.",
    roleCreated: "Rol creado.",
    roleDeleted: "Rol eliminado.",
    cannotDeleteSystemRole: "Los roles del sistema no se pueden eliminar.",
    roleHasEmployees: "No se puede eliminar: {{count}} empleado(s) activo(s) tienen este rol.",
    lockTooltip: "El acceso del administrador a Configuración no se puede eliminar.",
    sectionColumn: "Sección",
    unsavedChanges: "Cambios sin guardar",
    selectRolePrompt: "Selecciona un rol para ver y editar sus permisos.",
    rolesListHeading: "Roles",
    permissionsHeading: "Permisos",

    // Sistema de presets de Form Fields
    formPresets: {
      sectionHeading: "Elige el tipo de negocio que coincide con el tuyo",
      sectionSubtitle: "Primero elige el tipo de negocio, luego cuánto detalle quieres. Podrás ajustar campos individuales después.",
      choose: "Elegir",
      changeLevel: "Cambiar nivel",
      backToTypes: "Volver a tipos de negocio",
      levelHeading: "{{type}} — elige el nivel de detalle",
      levelSubtitle: "Elige el nivel que mejor refleje lo que tu negocio realmente necesita registrar.",
      pickLevel: "Elegir {{level}}",
      reapplyLevel: "Re-aplicar {{level}}",
      currentLevel: "Actual",
      currentlyApplied: "En uso",
      fieldCount: "{{count}} campo",
      fieldCount_other: "{{count}} campos",
      upToFields: "Hasta {{count}} campos",
      customTag: "personalizado",
      seeFields: "Ver campos",
      hideFields: "Ocultar campos",
      tabsYoullSee: "Pestañas que verás",
      tabPreview: {
        overview: "Resumen",
        sales: "Ventas",
        expenses: "Gastos",
        customers: "Clientes",
        inventory: "Inventario",
        suppliers: "Proveedores",
        catalog: "Catálogo",
        calendar: "Calendario",
        team: "Equipo",
      },
      levelDescription: {
        essential: "Los campos que la mayoría de los negocios de esta categoría realmente usan.",
        full: "Agrega los campos más profundos para usuarios avanzados — completo sin volverse exhaustivo.",
      },
      searchPlaceholder: "Buscar tipos de negocio…",
      comparePresets: "Comparar presets",
      customizeFields: "Personalizar campos",
      backToPreset: "Volver a {{type}} — {{level}}",
      resetToPreset: "Restablecer al preset",
      resetBannerBody: "Has personalizado esto desde el preset {{type}} — {{level}}.",
      applyTitle: "Aplicar {{type}} — {{level}}",
      applyBody: "Esto configurará todos los módulos para coincidir con el preset.",
      applyConfirm: "Aplicar preset",
      switchTitle: "Cambiar a {{type}} — {{level}}",
      switchWarning: "Cambiar de preset eliminará tus personalizaciones en todos los módulos y aplicará el nuevo preset.",
      switchConfirm: "Cambiar preset",
      previewLabel: "Vista previa",
      previewSubtitle: "Cómo se verá este formulario para tu equipo.",
      currentPresetLabel: "Preset actual",
      noPresetAppliedTitle: "Aún no hay preset aplicado",
      noPresetAppliedBody: "Elige un tipo de negocio para configurar tus formularios con un clic.",
      modifiedBadge: "Personalizado",
      levels: {
        essential: "Esencial",
        full: "Completo",
      },
      industry: {
        spa:        { name: "Spa",                                    tagline: "Tratamientos + citas" },
        salon:      { name: "Salón / Barbería",                       tagline: "Sin cita + citas" },
        gym:        { name: "Gimnasio / Estudio fitness",             tagline: "Membresías + clases" },
        ice_cream:  { name: "Heladería",                              tagline: "Mostrador + inventario de sabores" },
        cafe:       { name: "Cafetería",                              tagline: "Servicio rápido + preparación diaria" },
        bakery:     { name: "Panadería",                              tagline: "Pan diario + pedidos especiales" },
        restaurant: { name: "Restaurante",                            tagline: "Servicio en mesa + ingredientes" },
        retail:     { name: "Tienda al por menor",                    tagline: "Catálogo + stock por SKU" },
        consulting: { name: "Consultoría / Agencia",                  tagline: "Clientes por proyecto + facturación" },
        tutoring:   { name: "Tutoría / Coaching",                     tagline: "Sesiones recurrentes + estudiantes" },
        repair:     { name: "Reparación / Servicio de campo",         tagline: "Trabajos de campo + repuestos" },
        clinic:     { name: "Clínica / Consultorio médico",           tagline: "Citas + expedientes de pacientes" },
        general:    { name: "General",                                tagline: "Predeterminado" },
      },
      compare: {
        title: "Comparar presets",
        addPreset: "Agregar preset a comparar",
        clearAll: "Limpiar",
        empty: "Selecciona al menos un preset para comparar.",
        statusHidden: "Oculto",
        statusVisible: "Visible",
        statusRequired: "Obligatorio",
      },
      forcePicker: {
        title: "Elige tu tipo de negocio",
        body: "Tus campos actuales se conservan como personalizaciones sobre el preset que elijas. Puedes cambiar de preset luego.",
        browse: "Ver presets",
        keep: "Mantener lo actual",
      },
      onboarding: {
        skip: "Omitir por ahora — usar el predeterminado General",
      },
    },
    sectionDashboard: "Panel",
    sectionCustomers: "Clientes",
    sectionSuppliers: "Proveedores",
    sectionSales: "Ventas",
    sectionExpenses: "Gastos",
    sectionCatalog: "Catálogo",
    sectionInventory: "Inventario",
    sectionEmployees: "Empleados",
    sectionSettings: "Configuración",
    sectionCalendar: "Calendario",
    sectionNotifications: "Notificaciones",
    templateBasedOn: "Basado en {{name}}",
    templateModified: "modificado",
    resetToTemplate: "Restablecer plantilla",
    templateNone: "En blanco",
    templateAdmin: "Administrador",
    templateManager: "Gerente",
    templateSalesRep: "Vendedor",
    templatePickerLabel: "Empezar desde",
    localizationSection: "Regional",
    currency: "Moneda",
    timezone: "Zona horaria",
    language: "Idioma",
    languageEnglish: "Inglés",
    languageSpanish: "Español",
    localizationHint: "Moneda, zona horaria e idioma predeterminado para quienes trabajan en este negocio.",
  },
  export: {
    title: "Exportar datos",
    subtitle: "Elige un rango de fechas y formato.",
    startDate: "Fecha de inicio",
    endDate: "Fecha de fin",
    format: "Formato",
    download: "Descargar",
    downloading: "Descargando…",
    success: "Archivo descargado.",
    failed: "Error al exportar. Inténtalo de nuevo.",
  },
  platform: {
    title: "Analíticas de plataforma",
    subtitle: "Vista exclusiva del uso de la plataforma.",
  },
  errors: {
    loadFailed: "Error al cargar: {{detail}}",
    notFound: "No encontrado.",
  },
  landing: {
    meta: {
      title: "Adam — Administra tu negocio desde una sola plataforma",
      description:
        "Administra tu negocio desde una sola plataforma. Ventas, clientes, inventario, gastos y metas — toda tu información lista para crecer. Gratis durante el acceso anticipado.",
    },
    consent: {
      aria: "Aviso de analítica",
      message:
        "Usamos analítica propia para entender cómo se usa nuestra página y mejorar el producto — sin rastreadores de terceros ni de publicidad. Puedes darte de baja en cualquier momento.",
      gotIt: "Entendido",
      manage: "Gestionar",
      learnMore: "Política de Cookies",
    },
    cookiePrefs: {
      title: "Preferencias de cookies",
      intro:
        "Gestiona cómo Adam usa cookies y almacenamiento del navegador. Los elementos esenciales mantienen el sitio funcionando; la analítica es solo propia y nos ayuda a mejorar el producto.",
      essentialLabel: "Esenciales",
      essentialDesc:
        "Requeridas para el inicio de sesión, la seguridad y recordar tus preferencias. Siempre activas.",
      analyticsLabel: "Analítica",
      analyticsDesc:
        "Medición de audiencia propia de nuestras páginas de marketing. Sin terceros ni rastreo entre sitios.",
      save: "Guardar preferencias",
      acceptAll: "Aceptar todo",
      essentialOnly: "Solo esenciales",
      close: "Cerrar",
    },
    nav: {
      features: "Funciones",
      tour: "Recorrido",
      verticals: "Industrias",
      earlyAccess: "Acceso anticipado",
      faq: "Preguntas",
      login: "Iniciar sesión",
      getStarted: "Empieza gratis",
    },
    hero: {
      badge: "Acceso anticipado — gratis para todos los negocios",
      headlineLine1: "Administra tu negocio",
      headlineAccent: "desde una sola plataforma.",
      subheadline: "Toda tu información — ventas, clientes, inventario, gastos y metas — disponible al instante y lista para hacerte crecer.",
      ctaPrimary: "Empieza gratis hoy",
      ctaSecondary: "Explora el producto",
      earlyAccessNote: "Gratis durante el acceso anticipado · todos los módulos incluidos · sin tarjeta",
      proof: ["8 módulos conectados", "Todo tu equipo incluido", "Exporta tus datos cuando quieras"],
    },
    demo: {
      labels: {
        revenueTrend: "Tendencia de ingresos",
        paid: "Pagada",
        partial: "Parcial",
        unpaid: "Pendiente",
      },
    },
    bento: {
      rent: "Renta",
      supplies: "Insumos",
      goal: "Meta del mes",
      admin: "Admin",
      sales: "Vendedor",
    },
    verticals: {
      tag: "Hecho para tu industria",
      title: "¿A qué se dedica tu negocio?",
      subtitle: "Elige tu giro y mira cómo se ve Adam para un negocio como el tuyo — al registrarte precargamos productos de ejemplo y una meta inicial acordes.",
      tiles: {
        retail: {
          title: "Retail y tiendas",
          headline: "Deja de cuadrar inventario con capturas de WhatsApp.",
          body: "Vende en caja, controla stock en tiempo real y sabe qué producto paga la renta — sin pelearte con hojas de cálculo.",
          bullets: [
            "Inventario vivo en cada venta",
            "Pedidos a proveedores + alertas de stock",
            "Historial del cliente con un toque",
          ],
        },
        food_beverage: {
          title: "Alimentos y bebidas",
          headline: "Conoce tu costo antes de cerrar el mes.",
          body: "Proveedores, gastos y ventas del día en un solo lugar. Detecta el insumo que se está comiendo tu margen.",
          bullets: [
            "Gastos y proveedores integrados",
            "Corte del día en el que puedes confiar",
            "Inventario de insumos bajo control",
          ],
        },
        healthcare: {
          title: "Salud y clínicas",
          headline: "Tu consulta organizada, sin perseguir pagos.",
          body: "Pacientes, citas y cobros en un solo lugar — con el historial de cada paciente a un toque.",
          bullets: [
            "Historial y datos de cada paciente",
            "Citas y recordatorios en el calendario",
            "Pagos y saldos pendientes claros",
          ],
        },
        consulting: {
          title: "Consultoría y servicios",
          headline: "Cobra cada proyecto a tiempo, sin laberintos de archivos.",
          body: "Clientes, proyectos y cobranza organizados — con los números reales de cada proyecto siempre visibles.",
          bullets: [
            "Clientes y proyectos en orden",
            "Pagos parciales y saldos claros",
            "Metas de facturación que se siguen solas",
          ],
        },
        marketing: {
          title: "Marketing y agencias",
          headline: "El margen real de cada cuenta, sin abrir cinco archivos.",
          body: "Factura por cliente, controla gastos por campaña y sabe qué cuenta sí deja dinero.",
          bullets: [
            "Ingresos y gastos por cliente",
            "Servicios recurrentes bajo control",
            "Metas y reportes para tu equipo",
          ],
        },
        technology: {
          title: "Tecnología",
          headline: "Licencias, servicios y clientes en un sistema limpio.",
          body: "Cobros recurrentes, saldos pendientes y crecimiento mensual — visibles sin armar un tablero a mano.",
          bullets: [
            "Servicios y licencias recurrentes",
            "Saldos sin perseguir correos",
            "Crecimiento mensual en tu tablero",
          ],
        },
      },
      footnote: "¿Otro giro? Adam funciona para cualquier negocio pequeño — los ejemplos son solo un empujón inicial.",
    },
    capabilities: {
      tag: "Funciones",
      title: "Todo lo que toca tu negocio, en un solo lugar.",
      subtitle: "Empieza con ventas y clientes — activa el resto cuando lo necesites.",
      items: {
        dashboard: { title: "Tablero en vivo", body: "Ingresos, gastos y tendencias, actualizados con cada venta." },
        sales: { title: "Ventas y pagos", body: "Partidas, pagos parciales y cada peso pendiente — bajo control." },
        customers: { title: "Clientes", body: "Perfiles, historial de compras y valor total a un toque." },
        inventory: { title: "Inventario y catálogo", body: "Productos con precios y stock que se actualiza solo con cada venta." },
        expenses: { title: "Gastos", body: "Gastos únicos y recurrentes, con sus comprobantes." },
        team: { title: "Equipo, roles y metas", body: "Invita a tu equipo con los permisos correctos y define metas que se siguen solas." },
      },
    },
    tour: {
      tag: "Recorrido del producto",
      title: "Así se ve tu negocio, de un vistazo.",
      subtitle: "Pantallas reales de negocios de demostración — el mismo tablero que tienes desde el primer día.",
      expand: "Ver en tamaño completo",
      close: "Cerrar",
      shots: {
        overview: {
          label: "Tablero",
          title: "Todos los números que importan, en una pantalla.",
          body: "Ingresos, gastos, utilidad y metas activas — actualizados con cada venta que registra tu equipo, filtrados por cualquier rango de fechas.",
          alt: "Tablero principal de Adam con indicadores de ingresos y gastos, gráfica de tendencia y metas activas",
        },
        team: {
          label: "Equipo",
          title: "Colabora con todo tu equipo.",
          body: "Invita a tus empleados con el rol correcto — administrador, gerente, vendedor o los tuyos propios — para que cada quien vea justo lo que necesita y cada venta que registren sume al mismo panorama.",
          alt: "Página de empleados con los miembros del equipo, sus roles y permisos",
        },
        sales: {
          label: "Ventas",
          title: "Cada venta, pago y saldo — bajo control.",
          body: "Registra ventas con partidas y pagos parciales, ve qué está pagado y qué te deben, y no pierdas de vista ni una cuenta por cobrar.",
          alt: "Sección de ventas con montos, estado de pago y saldos",
        },
        calendar: {
          label: "Calendario",
          title: "La agenda de tu negocio, en el mismo lugar.",
          body: "Entregas, citas y eventos del equipo en un solo calendario — conectados a las ventas y clientes a los que pertenecen.",
          alt: "Vista mensual del calendario con eventos y citas del negocio",
        },
        goals: {
          label: "Metas",
          title: "Define metas que se siguen solas.",
          body: "Ingresos del mes, número de ventas, clientes nuevos — elige un objetivo y mira el avance actualizarse con cada venta, sin hojas de cálculo.",
          alt: "Página de metas con metas activas y barras de progreso que se actualizan solas",
        },
      },
    },
    how: {
      tag: "Cómo funciona",
      title: "Del registro a tu primera venta en 3 minutos.",
      steps: [
        { title: "Regístrate", body: "Correo y contraseña. Una pantalla." },
        { title: "Elige tu industria", body: "Preconfiguramos categorías, productos y una meta inicial." },
        { title: "Tour de 60 segundos", body: "Un recorrido rápido por todo." },
        { title: "Registra tu primera venta", body: "Tu tablero cobra vida. Lo demás es opcional." },
      ],
    },
    earlyAccess: {
      tag: "Acceso anticipado",
      title: "Gratis mientras lo construimos contigo.",
      subtitle: "Adam está en acceso anticipado: cada negocio recibe el producto completo, gratis.",
      badge: "Acceso anticipado",
      price: "Gratis",
      priceNote: "durante el acceso anticipado — todos los módulos incluidos",
      features: [
        "Los 8 módulos, sin candados",
        "Ventas, clientes y productos ilimitados",
        "Todo tu equipo, con roles incluidos",
        "Tableros en vivo y metas",
        "Lista en minutos",
        "Exporta tus datos cuando quieras",
      ],
      cta: "Crea tu cuenta gratis",
      finePrint: "Sin tarjeta. Sin sorpresas — siempre te avisaremos antes de cualquier cambio.",
      afterNote: "Los planes de pago se anunciarán con mucha anticipación. Los usuarios del acceso anticipado se enteran primero.",
    },
    faq: {
      tag: "Preguntas frecuentes",
      title: "Preguntas, respondidas.",
      items: [
        {
          q: "¿Adam es realmente gratis ahora?",
          a: "Sí. Adam está en acceso anticipado y todas las funciones son gratis mientras pulimos el producto con nuestros primeros negocios. Sin tarjeta y sin letras chiquitas.",
        },
        {
          q: "¿Qué pasa cuando termine el acceso anticipado?",
          a: "Anunciaremos los planes de pago con mucha anticipación y los usuarios del acceso anticipado se enterarán primero. Nunca se te cobrará en silencio y puedes exportar tus datos en cualquier momento.",
        },
        {
          q: "¿Puedo irme cuando quiera?",
          a: "Sí. Puedes dejar de usar Adam cuando quieras y exportar tu información (CSV/Excel) antes de irte. Tus datos son tuyos.",
        },
        {
          q: "¿Mis datos están seguros?",
          a: "Los cuidamos: las conexiones van cifradas, el acceso se protege con roles y permisos, y solo tu equipo puede ver tu negocio. Los detalles están en nuestro Aviso de Privacidad y Términos del Servicio.",
        },
        {
          q: "¿Cuánto tarda la configuración?",
          a: "Minutos. Elige tu giro y Adam precarga productos y categorías de ejemplo para que registres tu primera venta de inmediato — y borra los ejemplos cuando termines de explorar.",
        },
        {
          q: "¿Mi equipo también puede usarlo?",
          a: "Sí — invita a tus empleados por correo y asigna a cada quien un rol (admin, gerente o vendedor, o tus propios roles) para que vean exactamente lo que les toca.",
        },
        {
          q: "¿Funciona en mi teléfono y en español?",
          a: "Ambos. Adam corre en el navegador de tu teléfono, tablet o computadora, en español e inglés — cambia de idioma cuando quieras.",
        },
      ],
    },
    finalCta: {
      eyebrow: "Acceso anticipado · gratis · sin tarjeta",
      title: "Deja de hacer malabares.",
      titleLine2: "Lleva tu negocio en una sola pantalla.",
      subtitle: "Únete a los negocios que están cambiando hojas de cálculo, chats y libretas por Adam — gratis durante el acceso anticipado.",
      button: "Empieza gratis",
      secondary: "Escríbenos",
    },
    footer: {
      tagline: "Un solo lugar para tus ventas, clientes, inventario y metas — hecho para negocios pequeños.",
      columns: {
        product: "Producto",
        legal: "Legal",
        contact: "Contacto",
      },
      links: {
        features: "Funciones",
        verticals: "Soluciones por industria",
        earlyAccess: "Acceso anticipado",
        faq: "Preguntas frecuentes",
        consulting: "Consultoría",
        contact: "Contáctanos",
        terms: "Términos del Servicio",
        privacy: "Aviso de Privacidad",
        aup: "Uso Aceptable",
        dpa: "Tratamiento de Datos",
        subprocessors: "Subencargados",
        cookies: "Cookies",
        cookiePreferences: "Preferencias de cookies",
        login: "Iniciar sesión",
      },
      copyright: "© {{year}} Adam · Innbuilder. Todos los derechos reservados.",
    },
  },
  onboarding: {
    badge: "Configuración inicial",
    title: "Configuremos tu espacio en Adam",
    subtitle: "Tres respuestas rápidas y estás dentro. Lo demás lo pulimos después desde el tablero.",
    fields: {
      name: "Nombre del negocio",
      industry: "Elige una industria",
      country: "País",
      currency: "Moneda",
      timezone: "Zona horaria",
      language: "Idioma",
    },
    placeholders: {
      name: "p. ej. Café Luna",
    },
    countryOther: "Otro",
    timezoneOther: "Otra…",
    timezoneSearch: "Buscar zonas horarias…",
    setupError: {
      title: "No pudimos cargar tu espacio de trabajo",
      body: "Algo salió mal al conectar con el servidor. Tus datos están seguros — inténtalo de nuevo en un momento.",
      retry: "Reintentar",
      retrying: "Reintentando…",
    },
    presetSkipApplied:
      "Aplicamos los formularios predeterminados General / Esencial. Cámbialos cuando quieras en Configuración → Formularios.",
    timezoneHelp: "La usamos para todo lo que muestre fecha u hora.",
    currencyHelp: "Se usa en cada monto que registres. Puedes cambiarla luego desde Configuración.",
    languageHelp: "Idioma predeterminado de la interfaz para todos en este negocio. Cada usuario puede ajustar su preferencia.",
    validation: {
      nameRequired: "Dale un nombre a tu negocio para continuar.",
      industryRequired: "Elige la industria más cercana para configurarte.",
    },
    created: "¡Bienvenido!",
    creating: "Preparando todo…",
    continue: "Continuar",
    privacyNote: "Al continuar aceptas que tus datos se procesen conforme a la Política de Privacidad. Eres dueño de todo lo que ingresas — puedes exportarlo o eliminarlo cuando quieras.",
    nextStepsHint: "Incluido automáticamente",
    deferred: {
      starterData: "Una configuración inicial si eliges la opción de muestras",
      tour: "Un tour de 60 segundos si eliges la opción guiada",
      customize: "Logo, tema e invitaciones — tarjetas de un clic en tu tablero",
    },
    modeModal: {
      title: "¿Cómo quieres empezar?",
      subtitle: "Elige uno — puedes cambiarlo cuando quieras desde tu tablero.",
      blank: {
        title: "Empezar en blanco",
        body: "Un espacio limpio. Agrega tus propios clientes, productos y ventas.",
      },
      samples: {
        title: "Explorar con muestras",
        body: "Cargamos clientes, productos y ventas de muestra para que explores. Puedes borrarlos con un clic más tarde.",
      },
      guided: {
        title: "Tour guiado",
        body: "Te llevamos por las secciones clave en 60 segundos.",
      },
      saving: "Configurando…",
    },
    industries: {
      retail: {
        label: "Retail y tiendas",
        examples: "Boutique · Ferretería · Abarrotes",
      },
      food_beverage: {
        label: "Alimentos y bebidas",
        examples: "Café · Restaurante · Panadería",
      },
      services: {
        label: "Servicios",
        examples: "Salón · Barbería · Estudio",
      },
      healthcare: {
        label: "Salud y bienestar",
        examples: "Clínica · Dentista · Spa",
      },
      education: {
        label: "Educación",
        examples: "Academia · Tutor · Escuela",
      },
      consulting: {
        label: "Consultoría",
        examples: "Agencia · Freelance · Asesoría",
      },
      construction: {
        label: "Construcción y oficios",
        examples: "Contratista · Taller · Mantenimiento",
      },
      marketing: {
        label: "Marketing y creativo",
        examples: "Estudio · Foto · Video",
      },
      other: {
        label: "Otra cosa",
        examples: "Cuéntanos desde el tablero",
      },
    },
    checklist: {
      title: "Primeros pasos",
      subtitle: "Una lista rápida para hacer Adam tuyo",
      progress: "{{done}} de {{total}} listo",
      collapsed: "Primeros pasos · {{percent}}%",
      dismissHint: "Ocultar hasta terminar",
      items: {
        has_customer: { title: "Agrega tu primer cliente real", cta: "Agregar cliente" },
        has_product: { title: "Agrega un producto o servicio", cta: "Abrir catálogo" },
        has_sale: { title: "Registra tu primera venta", cta: "Registrar venta" },
        has_teammate: { title: "Invita a un compañero", cta: "Invitar" },
        has_goal: { title: "Define una meta del mes", cta: "Agregar meta" },
        has_logo: { title: "Sube tu logo", cta: "Subir" },
      },
    },
    starterBanner: {
      title: "Datos de muestra",
      body: "Precargamos algunos elementos para que tu tablero se vea vivo. Bórralos cuando quieras — un clic.",
      cta: "Borrar muestras",
      clearing: "Borrando…",
      cleared: "Muestras eliminadas.",
    },
    tour: {
      skip: "Saltar tour",
      next: "Siguiente",
      back: "Atrás",
      finish: "Listo",
      step: "Paso {{n}} de {{total}}",
      steps: {
        welcome: {
          title: "Bienvenido a Adam",
          body: "Un tour de 60 segundos por tu espacio. Puedes reabrirlo cuando quieras desde el tablero.",
        },
        sidebar: {
          title: "Tus módulos",
          body: "Todo lo que tu negocio necesita — clientes, ventas, inventario, metas — un menú, un login.",
        },
        dashboard: {
          title: "Tablero en vivo",
          body: "Cada métrica se actualiza al registrar actividad. Sin esperas ni exports.",
        },
        add: {
          title: "Alta rápida",
          body: "Crea cliente, venta o gasto en segundos desde cualquier parte.",
        },
        checklist: {
          title: "Lista de primeros pasos",
          body: "Marca las primeras cinco acciones y tu tablero estará listo para trabajo real.",
        },
      },
    },
    deferredCards: {
      title: "Personalízalo después",
      subtitle: "Pasos opcionales — o sáltalos para siempre.",
      dismissCard: "Descartar esta tarjeta",
      logo: { title: "Sube tu logo", body: "Aparece en recibos, facturas y el menú.", cta: "Subir" },
      theme: { title: "Elige tus colores", body: "Aplica tu marca en todo el producto.", cta: "Personalizar" },
      address: { title: "Agrega tu domicilio", body: "Se usa en facturas y recibos.", cta: "Agregar" },
      invites: { title: "Invita a tu equipo", body: "Roles y permisos listos de fábrica.", cta: "Invitar" },
      dismiss: "Ahora no",
      dismissed: "Oculto. Puedes reabrirlo desde Ajustes → Espacio de trabajo.",
    },
  },
  legal: {
    common: {
      legalCenter: "Centro Legal",
      contents: "Contenido",
      backToHome: "Volver al inicio",
      lastUpdatedOn: "Actualizado el {{date}}",
    },
    nav: {
      terms: "Términos del Servicio",
      privacy: "Política de Privacidad",
      aup: "Uso Aceptable",
      dpa: "Tratamiento de Datos",
      subprocessors: "Subencargados",
      cookies: "Política de Cookies",
    },
    subprocessors: {
      col: {
        provider: "Proveedor",
        purpose: "Finalidad",
        data: "Categorías de datos",
        region: "Región",
      },
    },
    cookies: {
      col: {
        name: "Cookie",
        provider: "Proveedor",
        purpose: "Propósito",
        category: "Categoría",
        duration: "Duración",
      },
      cat: {
        essential: "Esencial",
        functional: "Funcional",
        analytics: "Analítica",
      },
    },
    acceptance: {
      checkboxLabel: "Acepto los <terms>Términos del Servicio</terms> y la <privacy>Política de Privacidad</privacy>.",
      checkboxRequired: "Debes aceptar los Términos y la Política de Privacidad para continuar.",
      updatedTitle: "Actualizamos nuestros documentos legales",
      updatedBody: "Nuestros Términos del Servicio y Política de Privacidad se actualizaron. Revísalos y acéptalos para continuar usando Adam.",
      reviewTerms: "Revisar Términos",
      reviewPrivacy: "Revisar Privacidad",
      acceptAndContinue: "Aceptar y continuar",
      logout: "Cerrar sesión",
    },
  },
  terms: {
    nav: {
      backToHome: "Volver al inicio",
    },
    title: "Términos y Condiciones",
    lastUpdated: "Última actualización: 15 de abril de 2026",
    tableOfContents: "Tabla de contenido",
    sections: {
      s1Title: "1. Aceptación de los términos",
      s1Body: "Al acceder o usar la plataforma Adam (\"Servicio\"), operada por [FULL_LEGAL_NAME], persona física inscrita ante el SAT bajo el Régimen Simplificado de Confianza (RFC: [RFC]; domicilio fiscal: [FISCAL_ADDRESS], [CITY], [STATE], México), quien actúa comercialmente bajo el nombre comercial \"Innbuilder\" (\"Innbuilder\", \"nosotros\" o \"nuestro\"), aceptas estar sujeto a estos Términos y Condiciones (\"Términos\"). El Servicio se ofrece exclusivamente a personas con actividad empresarial para su uso comercial (B2B). Si no estás de acuerdo con todos estos Términos, no accedas ni uses el Servicio.\n\nEstos Términos se aplican a todos los usuarios del Servicio, incluyendo sin limitación a usuarios que navegan, clientes, comerciantes y contribuyentes de contenido. Al usar el Servicio, declaras tener al menos 18 años de edad y la autoridad legal para celebrar un acuerdo vinculante.",
      s2Title: "2. Descripción de los servicios",
      s2Body: "Adam es una plataforma de gestión empresarial en la nube que proporciona herramientas para gestionar clientes, ventas, gastos, inventario, catálogos, empleados y analíticas empresariales (\"Servicio\"). El Servicio se ofrece mediante suscripción a través de nuestro sitio web y aplicaciones web asociadas.\n\nNos reservamos el derecho de modificar, suspender o interrumpir el Servicio (o cualquier parte del mismo) en cualquier momento con o sin aviso previo. No seremos responsables ante ti ni ante ningún tercero por cualquier modificación, suspensión o interrupción del Servicio.\n\nCiertas funciones del Servicio pueden estar sujetas a términos o directrices adicionales, que se incorporan aquí por referencia.",
      s3Title: "3. Cuentas de usuario y registro",
      s3Body: "Para acceder al Servicio, debes registrar una cuenta proporcionando información precisa, actual y completa. Eres responsable de mantener la confidencialidad de las credenciales de tu cuenta y de todas las actividades que ocurran bajo tu cuenta.\n\nAceptas notificarnos de inmediato a support@adambusinesshub.com sobre cualquier uso no autorizado de tu cuenta o cualquier otra violación de seguridad. No podemos ni seremos responsables de ninguna pérdida o daño derivado de tu incumplimiento de esta obligación de seguridad.\n\nNo puedes usar la cuenta de otra persona sin permiso. El registro de cuenta está limitado a una cuenta por persona; no puedes crear múltiples cuentas.\n\nNos reservamos el derecho de rechazar el servicio, terminar cuentas, o eliminar o editar contenido a nuestra entera discreción.",
      s4Title: "4. Suscripción y condiciones de pago",
      s4Body: "El acceso a ciertas funciones del Servicio requiere una suscripción de pago. Las cuotas de suscripción se cobran por adelantado de forma mensual o anual, según el plan que selecciones. Todas las cuotas no son reembolsables, excepto lo expresamente indicado en estos Términos o según lo requiera la ley aplicable.\n\nUtilizamos procesadores de pago de terceros (incluido Stripe) para gestionar la facturación. Al proporcionar información de pago, nos autorizas a cobrar las tarifas aplicables a tu método de pago. Declaras tener la autoridad legal para usar el método de pago proporcionado.\n\nLas tarifas de suscripción están sujetas a cambios. Te notificaremos de cualquier cambio de tarifa con al menos 30 días de anticipación. El uso continuado del Servicio después de que el cambio de tarifa entre en vigor constituye tu aceptación de pagar la tarifa modificada.\n\nSi tu pago falla, podemos suspender o terminar tu acceso al Servicio hasta que se reciba el pago. Los montos vencidos pueden estar sujetos a procedimientos de cobranza.",
      s5Title: "5. Términos de la prueba gratuita",
      s5Body: "Ofrecemos un período de prueba gratuita de tres (3) meses para nuevos suscriptores. Durante la prueba gratuita, tendrás acceso a las funciones incluidas en tu plan seleccionado sin costo.\n\nAl final del período de prueba gratuita, tu suscripción se convertirá automáticamente en una suscripción de pago a menos que canceles antes de que expire la prueba. Se te cobrará la tarifa de suscripción aplicable al comienzo del primer ciclo de facturación siguiente a la prueba gratuita.\n\nNos reservamos el derecho de modificar o terminar la oferta de prueba gratuita en cualquier momento sin previo aviso. Solo se permite una prueba gratuita por entidad empresarial o individuo.",
      s6Title: "6. Política de uso aceptable",
      s6Body: "Aceptas usar el Servicio solo para fines lícitos y de acuerdo con estos Términos. Aceptas no usar el Servicio: (a) de ninguna manera que viole cualquier ley o regulación local, nacional o internacional aplicable; (b) para transmitir publicidad o material promocional no solicitado o no autorizado; (c) para suplantar o intentar suplantar a la Empresa, a un empleado de la Empresa, a otro usuario o a cualquier otra persona; (d) para participar en cualquier conducta que restrinja o inhiba el uso o disfrute del Servicio por parte de cualquier persona.\n\nAceptas no: cargar o transmitir virus u otro tipo de código malicioso; intentar obtener acceso no autorizado a cualquier parte del Servicio; usar cualquier robot, araña u otro dispositivo automático para acceder al Servicio con cualquier propósito; interferir o interrumpir la integridad o el rendimiento del Servicio.\n\nNos reservamos el derecho de terminar tu acceso al Servicio por violaciones de esta Política de uso aceptable.",
      s7Title: "7. Propiedad intelectual",
      s7Body: "El Servicio y su contenido original, características y funcionalidad son y seguirán siendo propiedad exclusiva de [FULL_LEGAL_NAME] (operando como \"Innbuilder\") y nuestros licenciantes. El Servicio está protegido por derechos de autor, marcas registradas y otras leyes de México y países extranjeros.\n\nLas marcas, imagen comercial y nombres comerciales de \"Innbuilder\" y \"Adam\" no pueden usarse en conexión con ningún producto o servicio sin nuestro consentimiento previo por escrito.\n\nConservas todos los derechos sobre los datos y el contenido que cargues al Servicio (\"Contenido del usuario\"). Al cargar Contenido del usuario, nos otorgas una licencia mundial, no exclusiva y libre de regalías para usar, copiar, procesar y mostrar tu Contenido del usuario únicamente con el propósito de proporcionarte el Servicio.\n\nDeclaras y garantizas que eres propietario o tienes los derechos necesarios sobre tu Contenido del usuario y que no infringe ningún derecho de propiedad intelectual de terceros.",
      s8Title: "8. Privacidad y datos",
      s8Body: "Tu privacidad es importante para nosotros. Nuestra Política de Privacidad, que se incorpora a estos Términos por referencia, explica cómo recopilamos, usamos y compartimos información sobre ti cuando usas el Servicio. Al usar el Servicio, consientes nuestra recopilación y uso de datos personales según se describe en nuestra Política de Privacidad.\n\nImplementamos medidas de seguridad estándar de la industria para proteger tus datos, incluido el cifrado en tránsito (TLS) y en reposo. Sin embargo, ningún método de transmisión por Internet o de almacenamiento electrónico es 100% seguro.\n\nPuedes solicitar la eliminación de tu cuenta y los datos asociados en cualquier momento contactándonos en support@adambusinesshub.com. Procesaremos las solicitudes de eliminación dentro de 30 días, sujeto a cualquier obligación legal de conservar ciertos datos.",
      s9Title: "9. Exenciones y garantías",
      s9Body: "EL SERVICIO SE PROPORCIONA \"TAL CUAL\" Y \"SEGÚN DISPONIBILIDAD\" SIN GARANTÍAS DE NINGÚN TIPO, YA SEA EXPRESA O IMPLÍCITA, INCLUYENDO PERO SIN LIMITARSE A GARANTÍAS IMPLÍCITAS DE COMERCIABILIDAD, IDONEIDAD PARA UN PROPÓSITO PARTICULAR O NO INFRACCIÓN.\n\nNo garantizamos que: (a) el Servicio funcione sin interrupciones, de forma segura o esté disponible en cualquier momento o lugar en particular; (b) se corrijan errores o defectos; (c) el Servicio esté libre de virus u otros componentes dañinos; (d) los resultados del uso del Servicio satisfagan tus requisitos.\n\nAlgunas jurisdicciones no permiten la exclusión de garantías implícitas, por lo que las exclusiones anteriores pueden no aplicarse a ti.",
      s10Title: "10. Limitación de responsabilidad",
      s10Body: "EN LA MÁXIMA MEDIDA PERMITIDA POR LA LEY APLICABLE, EN NINGÚN CASO INNBUILDER, SUS AFILIADOS, DIRECTORES, EMPLEADOS O LICENCIANTES SERÁN RESPONSABLES DE DAÑOS INDIRECTOS, INCIDENTALES, ESPECIALES, CONSECUENTES O PUNITIVOS, INCLUYENDO SIN LIMITACIÓN PÉRDIDA DE GANANCIAS, DATOS, USO, FONDO DE COMERCIO U OTRAS PÉRDIDAS INTANGIBLES, RESULTANTES DE: (i) TU ACCESO O USO O INCAPACIDAD DE ACCEDER O USAR EL SERVICIO; (ii) CUALQUIER CONDUCTA O CONTENIDO DE CUALQUIER TERCERO EN EL SERVICIO; (iii) CUALQUIER CONTENIDO OBTENIDO DEL SERVICIO; O (iv) ACCESO NO AUTORIZADO, USO O ALTERACIÓN DE TUS TRANSMISIONES O CONTENIDO.\n\nEN NINGÚN CASO NUESTRA RESPONSABILIDAD AGREGADA EXCEDERÁ EL MAYOR ENTRE CIEN DÓLARES (USD $100) O EL MONTO QUE NOS HAYAS PAGADO EN LOS DOCE (12) MESES ANTERIORES A LA RECLAMACIÓN.",
      s11Title: "11. Terminación",
      s11Body: "Podemos terminar o suspender tu cuenta y acceso al Servicio de inmediato, sin previo aviso ni responsabilidad, por cualquier razón, incluyendo sin limitación si incumples estos Términos.\n\nAl momento de la terminación, tu derecho a usar el Servicio cesará de inmediato. Si deseas terminar tu cuenta, puedes simplemente dejar de usar el Servicio o contactarnos en support@adambusinesshub.com.\n\nTodas las disposiciones de los Términos que por su naturaleza deberían sobrevivir a la terminación sobrevivirán, incluyendo sin limitación las disposiciones de propiedad, exenciones de garantía, indemnización y limitaciones de responsabilidad.\n\nUpon termination, we will retain your data for a period of 30 days, after which it may be permanently deleted.",
      s12Title: "12. Ley aplicable",
      s12Body: "Estos Términos se regirán e interpretarán de acuerdo con las leyes de México, sin tener en cuenta sus disposiciones sobre conflicto de leyes. Cualquier disputa que surja bajo o en conexión con estos Términos estará sujeta a la jurisdicción exclusiva de los tribunales ubicados en la Ciudad de México, México.\n\nSi accedes al Servicio desde fuera de México, lo haces bajo tu propio riesgo y eres responsable del cumplimiento de las leyes locales. No hacemos ninguna declaración de que el Servicio sea apropiado o esté disponible en ubicaciones fuera de México.\n\nLas partes acuerdan que la Convención de las Naciones Unidas sobre los Contratos de Compraventa Internacional de Mercaderías no se aplica a estos Términos.",
      s13Title: "13. Cambios a los términos",
      s13Body: "Nos reservamos el derecho de modificar o reemplazar estos Términos en cualquier momento a nuestra entera discreción. Si una revisión es sustancial, proporcionaremos al menos 30 días de aviso antes de que los nuevos términos entren en vigor. Lo que constituye un cambio sustancial se determinará a nuestra entera discreción.\n\nAl continuar accediendo o usando el Servicio después de que las revisiones entren en vigor, aceptas estar sujeto a los Términos revisados. Si no estás de acuerdo con los nuevos Términos, deja de usar el Servicio.\n\nIndicaremos la fecha en que estos Términos fueron revisados por última vez en la parte superior de esta página. Te recomendamos revisar estos Términos periódicamente para detectar cambios.",
      s14Title: "14. Información de contacto",
      s14Body: "Si tienes alguna pregunta sobre estos Términos y Condiciones, por favor contáctanos:\n\n[FULL_LEGAL_NAME] (persona física), operando como \"Innbuilder\"\nDomicilio fiscal: [FISCAL_ADDRESS], [CITY], [STATE], México · RFC [RFC]\nCorreo electrónico: support@adambusinesshub.com\nSitio web: adambusinesshub.com\n\nPara consultas de facturación: billing@adambusinesshub.com\nPara asuntos de privacidad: privacy@adambusinesshub.com\n\nHaremos todo lo posible por responder a tu consulta en un plazo de 5 días hábiles.",
    },
  },
  calendar: {
    title: "Calendario",
    subtitle: "Ventas, gastos, requerimientos de inventario y eventos en un solo lugar",
    newEvent: "Nuevo evento",
    editEvent: "Editar evento",
    deleteEvent: "Eliminar evento",
    deleteConfirm: "¿Seguro que deseas eliminar \"{{title}}\"?",
    eventCreated: "Evento creado",
    eventUpdated: "Evento actualizado",
    eventDeleted: "Evento eliminado",
    today: "Hoy",
    noItems: "Nada programado este día",
    unassigned: "Sin asignar",
    eventTitle: "Título",
    type: "Tipo",
    description: "Descripción",
    date: "Fecha",
    endDate: "Fecha fin",
    addEndDate: "Agregar fecha fin",
    addTime: "Agregar hora",
    removeTime: "Quitar hora",
    status: "Estado",
    assignedTo: "Asignado a",
    kinds: {
      sale: "Ventas",
      expense: "Gastos",
      inventory: "Inventario",
      event: "Eventos",
    },
    types: {
      appointment: "Cita",
      reminder: "Recordatorio",
      task: "Tarea",
      other: "Otro",
    },
    statuses: {
      pending: "Pendiente",
      in_progress: "En progreso",
      completed: "Completado",
      cancelled: "Cancelado",
    },
    inventoryStatuses: {
      low_stock: "Stock bajo",
      out_of_stock: "Sin stock",
    },
    allStatuses: "Todos",
    markComplete: "Marcar como completado",
    allDay: "Todo el día",
    balanceDue: "Adeuda {{amount}}",
    itemsCount_one: "{{count}} artículo",
    itemsCount_other: "{{count}} artículos",
    payLabel: "Pago",
    filterEvents: "Filtrar eventos:",
  },
  notifications: {
    title: "Notificaciones",
    subtitle: "Tu actividad y alertas",
    markAllRead: "Marcar todo como leído",
    markRead: "Marcar como leído",
    noNotifications: "Sin notificaciones",
    noUnread: "¡Estás al día!",
    viewAll: "Ver todas las notificaciones",
    viewRelated: "Ver registro relacionado",
    prefsSaved: "Preferencias guardadas",
    allStatuses: "Todos los estados",
    allCategories: "Todas las categorías",
    total: "notificaciones",
    preferences: "Preferencias de notificaciones",
    managePreferences: "Gestionar preferencias",
    emailEnabled: "Activar notificaciones por email",
    emailEnabledHint: "Recibe notificaciones por correo cuando esté activado por categoría",
    category: "Categoría",
    inApp: "En app",
    email: "Email",
    statuses: {
      unread: "No leída",
      read: "Leída",
      archived: "Archivada",
    },
    categories: {
      goal_achieved: "Meta alcanzada",
      expense_threshold: "Límite de gasto",
      payment_due: "Pago pendiente",
      payment_overdue: "Pago vencido",
      appointment_upcoming: "Cita próxima",
      reminder_upcoming: "Recordatorio próximo",
      calendar_change: "Cambio en calendario",
      sales_activity: "Actividad de ventas",
      deadline_proximity: "Plazo próximo",
      system: "Sistema",
    },
  },
  goals: {
    title: "Metas",
    subtitle: "Seguí tu avance hacia los objetivos.",
    newGoal: "Nueva meta",
    editGoal: "Editar meta",
    empty: "Aún no hay metas.",
    createFirst: "Crear la primera meta",
    allStatuses: "Todos los estados",
    allModules: "Todas las secciones",
    any: "Cualquiera",
    notifications: "Notificaciones",
    daysRemaining_one: "{{count}} día restante",
    daysRemaining_other: "{{count}} días restantes",
    windowClosed: "Ventana cerrada",
    confirmDelete: "¿Eliminar esta meta?",
    reevaluate: "Recalcular",
    reevaluating: "Recalculando…",
    reevaluateTitle: "Recalcular el avance de todas las metas activas",
    reevaluatedToast_one: "Se recalculó {{count}} meta.",
    reevaluatedToast_other: "Se recalcularon {{count}} metas.",
    atRisk: "En riesgo",
    filterStatus: "Estado",
    filterModule: "Sección",
    sortBy: "Ordenar por",
    emptyFiltered: "No hay metas que coincidan con los filtros.",
    clearFilters: "Limpiar filtros",
    statuses: {
      active: "Activa",
      achieved: "Lograda",
      missed: "No alcanzada",
      archived: "Archivada",
    },
    statusesDecrease: {
      active: "En curso",
      achieved: "Dentro del presupuesto",
      missed: "Excedido",
      archived: "Archivada",
    },
    direction: {
      label: "Dirección de la meta",
      increase: "Alcanzar este objetivo",
      increaseHint: "Más es mejor — celebrá al llegar al objetivo.",
      decrease: "Mantenerse bajo este tope",
      decreaseHint: "Menos es mejor — avisos al acercarte al tope, alerta si lo superás.",
    },
    targetLabel: {
      increase: "Valor objetivo",
      decrease: "Tope de gasto",
    },
    sections: {
      active: "Activas",
      closed: "Metas cerradas",
    },
    sort: {
      deadline: "Fecha límite",
      progress: "Avance",
    },
    kpi: {
      active: "Activas",
      atRisk: "En riesgo",
      achieved: "Logradas",
      missed: "No alcanzadas",
    },
    modules: {
      sales: "Ventas",
      customers: "Clientes",
      expenses: "Gastos",
      inventory: "Inventario",
      calendar: "Calendario",
      catalog: "Catálogo",
      employees: "Empleados",
      suppliers: "Proveedores",
    },
    fields: {
      name: "Nombre",
      description: "Descripción",
      metric: "Métrica",
      target: "Valor objetivo",
      start: "Fecha inicio",
      end: "Fecha fin",
    },
    placeholders: {
      name: "ej. 10 clientes nuevos en abril",
    },
    form: {
      sections: {
        measure: "Qué medir",
        goal: "Meta",
        window: "Período",
        notifications: "Notificaciones",
      },
    },
    notify: {
      achieved: "Avisar al lograr la meta (100%)",
      milestones: "Avisar en hitos (50%, 75%)",
      missed: "Avisar si no se alcanza al final",
      digest: "Incluir en resumen diario",
    },
    notifyDecrease: {
      achieved: "Avisar si me mantengo bajo el tope al cierre del período",
      milestones: "Avisar al 75% y 90% del tope",
      missed: "Alertar al instante si se supera el tope",
      digest: "Incluir en resumen diario",
    },
  },
};

export default es;
